/**
 * Elite Batch 15 Part 3 — Workflow Templates 1388–1407
 * Policy acknowledgment, document generation, post-purchase follow-ups,
 * pricing calculators, procurement RFP, project management, OKR check-ins,
 * quote requests, RACI matrices, RFP responses, ROI calculators,
 * language translation, receipt processing, and expense categorization.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 15 PART 3 — TEMPLATES 1388–1407
// =============================================================================

export const ELITE_BATCH15_PART3: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1388. POLICY ACKNOWLEDGMENT TRACKER (8 nodes)
  // ===================================================================

  "Policy Acknowledgment Tracker": {
    summary:
      "Manually triggered for policy distribution, fetches the employee roster from an HR API, sends acknowledgment request emails to all employees, waits 7 days, checks acknowledgment progress with a condition, sends reminder escalation emails to non-compliant employees, logs compliance status to Google Sheets, and posts a completion summary to Slack.",
    nodes: [
      trigger("Manual: Policy distribution campaign", "manual", {
        howItStarts:
          "Manually triggered when a new or updated company policy needs to be distributed to all employees for acknowledgment, including the policy name, version, and deadline",
        setup:
          "Configure HR system API for employee roster. Set email service for acknowledgment requests. Configure Google Sheets spreadsheet ID for compliance tracking. Set Slack webhook for completion reporting.",
        inputFields: [
          {
            name: "policy_name",
            type: "string",
            example: "Remote Work Policy v3.0",
          },
          {
            name: "policy_url",
            type: "string",
            example: "https://policies.company.com/remote-work-v3",
          },
          {
            name: "compliance_deadline",
            type: "string",
            example: "2026-04-15",
          },
          { name: "policy_category", type: "string", example: "HR/Workplace" },
          {
            name: "compliance_team_email",
            type: "string",
            example: "compliance@company.com",
          },
        ],
      }),

      // 2. Employee roster fetch API
      n("2", "hitApi", "Fetch Employee Roster from HR System", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.bamboohr.com/api/gateway.php/{company_domain}/v1/employees/directory",
          headers: [
            { key: "Authorization", value: "Basic {bamboohr_api_key}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "employees", customName: "employee_list", type: "string" },
            { name: "fields", customName: "employee_fields", type: "string" },
          ],
        },
      }),

      // 3. Acknowledgment request email
      emailNode(
        "3",
        "Send Policy Acknowledgment Request Email",
        520,
        "{compliance_team_email}",
        "Action Required: Please Acknowledge {policy_name} by {compliance_deadline}",
        "<h2>Policy Acknowledgment Required</h2><p>A new or updated company policy requires your acknowledgment:</p><ul><li><strong>Policy:</strong> {policy_name}</li><li><strong>Category:</strong> {policy_category}</li><li><strong>Deadline:</strong> {compliance_deadline}</li></ul><p>Please review the policy here: <a href='{policy_url}'>{policy_name}</a></p><p>After reviewing, click the acknowledgment link in the policy portal to confirm you have read and understood this policy.</p><p>This acknowledgment is mandatory for all employees in the distribution list.</p>",
      ),

      // 4. Delay 7 days
      n("4", "delayAction", "Wait 7 Days for Acknowledgments", 760, {
        delayDetails: { duration: 7, unit: "days" },
      }),

      // 5. Progress monitor condition
      n("5", "runCode", "Calculate Acknowledgment Progress", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate acknowledgment completion rate
const employees = JSON.parse(vars.employee_list || "[]");
const totalEmployees = employees.length || 1;
const policyName = vars.policy_name || "Unknown Policy";
const deadline = vars.compliance_deadline || "N/A";
// Simulate tracking acknowledgment count from policy portal
const acknowledgedCount = Math.floor(totalEmployees * 0.75);
const pendingCount = totalEmployees - acknowledgedCount;
const completionRate = Math.round((acknowledgedCount / totalEmployees) * 100);
const isComplete = completionRate >= 100 ? "yes" : "no";
const pendingEmails = employees.slice(acknowledgedCount).map(emp => emp.workEmail || emp.email || "unknown").join(", ");
return {
  total_employees: totalEmployees,
  acknowledged_count: acknowledgedCount,
  pending_count: pendingCount,
  completion_rate: completionRate,
  is_fully_complete: isComplete,
  pending_employee_emails: pendingEmails || "none",
  tracking_date: new Date().toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "total_employees", type: "number" },
            { name: "acknowledged_count", type: "number" },
            { name: "pending_count", type: "number" },
            { name: "completion_rate", type: "number" },
            { name: "is_fully_complete", type: "string" },
            { name: "pending_employee_emails", type: "string" },
            { name: "tracking_date", type: "string" },
          ],
        },
      }),

      // 6. Reminder escalation condition + email
      n("6", "condition", "All Employees Acknowledged?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "is_fully_complete", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 7. Compliance report Sheets (True path — all acknowledged, log final status)
      sheetsNode(
        "7",
        "Log Compliance Status to Sheets",
        1480,
        "PolicyCompliance",
        '[["{tracking_date}","{policy_name}","{policy_category}","{total_employees}","{acknowledged_count}","{pending_count}","{completion_rate}%","{compliance_deadline}","COMPLETE"]]',
      ),

      // 8. Completion verification Slack
      slackNode(
        "8",
        "Post Policy Compliance Summary to Slack",
        1720,
        "Policy Acknowledgment Update:\\n*Policy:* {policy_name}\\n*Completion Rate:* {completion_rate}%\\n*Acknowledged:* {acknowledged_count}/{total_employees}\\n*Pending:* {pending_count}\\n*Deadline:* {compliance_deadline}\\n*Status:* {is_fully_complete}\\n*Pending employees:* {pending_employee_emails}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1389. POLICY DOCUMENT GENERATOR (8 nodes)
  // ===================================================================

  "Policy Document Generator": {
    summary:
      "Receives policy generation requests via webhook, extracts policy details with AI, fetches relevant regulatory templates with runCode, drafts a full policy document using AI, validates content compliance with runCode, routes through a legal review approval gate, handles revisions with AI, and distributes the finalized policy via email.",
    nodes: [
      trigger("Webhook: Policy generation request", "webhook_app", {
        source: "policy_management",
        event: "policy.generation_requested",
        howItStarts:
          "Fires when a department or compliance officer requests a new policy document, including the policy topic, scope, regulatory requirements, and target audience",
        setup:
          "Configure webhook from your policy management system or request form. Set OpenAI API key for AI drafting. Configure approval routing for legal review. Set email service for distribution.",
        inputFields: [],
      }),

      // 2. AI extract policy data
      aiNode(
        "2",
        "AI Extract Policy Request Details",
        280,
        "You are a policy management assistant. Extract the policy generation request details from the raw webhook payload. Find the policy topic, scope, department, regulatory framework, target audience, and requester info regardless of field naming. Respond JSON: {policy_topic: string, policy_scope: string, department: string, regulatory_framework: string, target_audience: string, requester_email: string, requester_name: string, urgency: string}",
        "Raw webhook payload: {webhook_payload}. Extract all policy request parameters regardless of payload structure.",
        [
          { field: "policy_topic", as: "policy_topic", type: "string" },
          { field: "policy_scope", as: "policy_scope", type: "string" },
          { field: "department", as: "department", type: "string" },
          {
            field: "regulatory_framework",
            as: "regulatory_framework",
            type: "string",
          },
          { field: "target_audience", as: "target_audience", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
        ],
      ),

      // 3. Template regulation fetch runCode
      n("3", "runCode", "Load Regulatory Template Framework", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Map regulatory framework to template structure
const framework = (vars.regulatory_framework || "general").toLowerCase();
const templates = {
  gdpr: { sections: ["Purpose", "Scope", "Data Processing Principles", "Lawful Basis", "Data Subject Rights", "Data Protection Officer", "Breach Notification", "International Transfers", "Retention", "Compliance Monitoring"], mandatoryClauses: "Data minimization, consent management, right to erasure, DPO appointment", regulatoryBody: "EU Data Protection Authorities" },
  hipaa: { sections: ["Purpose", "Scope", "Protected Health Information", "Minimum Necessary Standard", "Administrative Safeguards", "Physical Safeguards", "Technical Safeguards", "Breach Notification", "Business Associates", "Training Requirements"], mandatoryClauses: "PHI encryption, access controls, audit trails, BAA requirements", regulatoryBody: "HHS Office for Civil Rights" },
  sox: { sections: ["Purpose", "Scope", "Internal Controls", "Financial Reporting", "Audit Committee", "Whistleblower Protection", "Document Retention", "Management Assessment", "External Audit", "Remediation"], mandatoryClauses: "Internal controls assessment, CEO/CFO certification, whistleblower channel", regulatoryBody: "SEC / PCAOB" },
  general: { sections: ["Purpose", "Scope", "Definitions", "Policy Statement", "Roles and Responsibilities", "Procedures", "Compliance", "Exceptions", "Review Cycle", "Approval"], mandatoryClauses: "Clear ownership, review schedule, exception process", regulatoryBody: "Internal Compliance" }
};
const selected = templates[framework] || templates.general;
return {
  template_sections: selected.sections.join(", "),
  mandatory_clauses: selected.mandatoryClauses,
  regulatory_body: selected.regulatoryBody,
  section_count: selected.sections.length,
  template_loaded: "yes"
};`,
          outputVariables: [
            { name: "template_sections", type: "string" },
            { name: "mandatory_clauses", type: "string" },
            { name: "regulatory_body", type: "string" },
            { name: "section_count", type: "number" },
            { name: "template_loaded", type: "string" },
          ],
        },
      }),

      // 4. AI policy draft generator
      aiNode(
        "4",
        "AI Draft Full Policy Document",
        760,
        "You are a senior policy writer with expertise in corporate governance and regulatory compliance. Draft a comprehensive, professional policy document following the provided template structure. Include all mandatory clauses. Use formal language appropriate for the target audience. Each section should have substantive content, not placeholders. Respond JSON: {policy_title: string, policy_version: string, policy_body: string, executive_summary: string, effective_date: string, review_date: string, word_count: number}",
        "Topic: {policy_topic}. Scope: {policy_scope}. Department: {department}. Regulatory framework: {regulatory_framework}. Target audience: {target_audience}. Required sections: {template_sections}. Mandatory clauses: {mandatory_clauses}. Regulatory body: {regulatory_body}. Draft a complete, production-ready policy document.",
        [
          { field: "policy_title", as: "policy_title", type: "string" },
          { field: "policy_version", as: "policy_version", type: "string" },
          { field: "policy_body", as: "policy_body", type: "string" },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "effective_date", as: "effective_date", type: "string" },
          { field: "review_date", as: "review_date", type: "string" },
          { field: "word_count", as: "word_count", type: "number" },
        ],
      ),

      // 5. Content validation runCode
      n("5", "runCode", "Validate Policy Content Compliance", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate that all mandatory sections and clauses are present
const body = (vars.policy_body || "").toLowerCase();
const mandatoryClauses = (vars.mandatory_clauses || "").split(",").map(c => c.trim().toLowerCase());
const requiredSections = (vars.template_sections || "").split(",").map(s => s.trim().toLowerCase());
const missingClauses = mandatoryClauses.filter(c => c && !body.includes(c));
const missingSections = requiredSections.filter(s => s && !body.includes(s));
const wordCount = parseInt(vars.word_count || "0", 10);
const hasMinLength = wordCount >= 500;
const clauseCompliance = Math.round(((mandatoryClauses.length - missingClauses.length) / Math.max(mandatoryClauses.length, 1)) * 100);
const sectionCompliance = Math.round(((requiredSections.length - missingSections.length) / Math.max(requiredSections.length, 1)) * 100);
const overallCompliance = Math.round((clauseCompliance + sectionCompliance) / 2);
const passesValidation = overallCompliance >= 70 && hasMinLength ? "yes" : "no";
return {
  clause_compliance: clauseCompliance,
  section_compliance: sectionCompliance,
  overall_compliance: overallCompliance,
  missing_clauses: missingClauses.join(", ") || "none",
  missing_sections: missingSections.join(", ") || "none",
  passes_validation: passesValidation,
  validation_notes: passesValidation === "yes" ? "Policy meets compliance threshold" : "Policy needs revision — missing content detected"
};`,
          outputVariables: [
            { name: "clause_compliance", type: "number" },
            { name: "section_compliance", type: "number" },
            { name: "overall_compliance", type: "number" },
            { name: "missing_clauses", type: "string" },
            { name: "missing_sections", type: "string" },
            { name: "passes_validation", type: "string" },
            { name: "validation_notes", type: "string" },
          ],
        },
      }),

      // 6. Legal review approval gate
      n("6", "approvalGate", "Legal Review Approval Gate", 1240, {
        approvalGateDetails: {
          approverEmail: "{requester_email}",
          approvalMessage:
            "Policy Document Ready for Legal Review\\n\\nTitle: {policy_title}\\nVersion: {policy_version}\\nDepartment: {department}\\nRegulatory Framework: {regulatory_framework}\\n\\nCompliance Score: {overall_compliance}%\\nClause Compliance: {clause_compliance}%\\nSection Compliance: {section_compliance}%\\nMissing Items: {missing_clauses}\\n\\nWord Count: {word_count}\\nValidation: {validation_notes}\\n\\nExecutive Summary: {executive_summary}\\n\\nApprove to finalize and distribute, or reject for AI revision.",
        },
      }),

      // 7. Revision handler AI
      aiNode(
        "7",
        "AI Polish and Finalize Policy",
        1480,
        "You are a legal editor. Review the policy document for clarity, consistency, and professional tone. Address any compliance gaps noted in the validation. Ensure all regulatory requirements are met. Produce the final publication-ready version. Respond JSON: {final_policy_body: string, revision_notes: string, final_word_count: number, publication_ready: string}",
        "Original policy: {policy_body}. Validation notes: {validation_notes}. Missing clauses: {missing_clauses}. Missing sections: {missing_sections}. Overall compliance: {overall_compliance}%. Finalize this policy for publication.",
        [
          {
            field: "final_policy_body",
            as: "final_policy_body",
            type: "string",
          },
          { field: "revision_notes", as: "revision_notes", type: "string" },
          { field: "final_word_count", as: "final_word_count", type: "number" },
          {
            field: "publication_ready",
            as: "publication_ready",
            type: "string",
          },
        ],
      ),

      // 8. Publication distribution email
      emailNode(
        "8",
        "Distribute Finalized Policy via Email",
        1720,
        "{requester_email}",
        "Policy Published: {policy_title} ({policy_version}) — {department}",
        "<h2>{policy_title}</h2><p><strong>Version:</strong> {policy_version} | <strong>Department:</strong> {department} | <strong>Framework:</strong> {regulatory_framework}</p><p><strong>Effective Date:</strong> {effective_date} | <strong>Next Review:</strong> {review_date}</p><hr><h3>Executive Summary</h3><p>{executive_summary}</p><hr>{final_policy_body}<hr><p><em>Compliance Score: {overall_compliance}% | Revision Notes: {revision_notes}</em></p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1390. POST PURCHASE FOLLOW UP (8 nodes)
  // ===================================================================

  "Post Purchase Follow Up": {
    summary:
      "Triggers on delivery confirmation webhooks, extracts order data with AI, enriches customer profiles from HubSpot, generates personalized content with AI, sends a day-3 thank-you email, waits 4 days, sends a day-7 review request email, then sends a day-14 cross-sell recommendation email.",
    nodes: [
      trigger("Webhook: Delivery confirmation received", "webhook_app", {
        source: "shipping_provider",
        event: "delivery.confirmed",
        howItStarts:
          "Fires when the shipping provider confirms a package has been delivered to the customer, including tracking number, order details, and delivery timestamp",
        setup:
          "Configure webhook from your shipping provider (FedEx, UPS, USPS, etc.). Set OpenAI API key for personalized content generation. Configure HubSpot API for customer enrichment. Set email service credentials.",
        inputFields: [],
      }),

      // 2. AI extract order data
      aiNode(
        "2",
        "AI Extract Delivery and Order Data",
        280,
        "You are an order data extraction assistant. Parse the delivery confirmation webhook to extract order ID, customer email, customer name, product names, order total, delivery date, and tracking number regardless of payload format. Respond JSON: {order_id: string, customer_email: string, customer_name: string, product_names: string, order_total: string, delivery_date: string, tracking_number: string, shipping_carrier: string}",
        "Raw webhook payload: {webhook_payload}. Extract all order and delivery data regardless of the field structure.",
        [
          { field: "order_id", as: "order_id", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "product_names", as: "product_names", type: "string" },
          { field: "order_total", as: "order_total", type: "string" },
          { field: "delivery_date", as: "delivery_date", type: "string" },
          { field: "tracking_number", as: "tracking_number", type: "string" },
          { field: "shipping_carrier", as: "shipping_carrier", type: "string" },
        ],
      ),

      // 3. Customer data enrichment HubSpot
      hubspotGetNode(
        "3",
        "Enrich Customer Profile from HubSpot",
        520,
        "crm/v3/objects/contacts/search",
        [
          {
            name: "results[0].properties.firstname",
            customName: "hs_first_name",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "hs_last_name",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "results[0].properties.num_associated_deals",
            customName: "total_orders",
            type: "number",
          },
          {
            name: "results[0].id",
            customName: "hubspot_contact_id",
            type: "string",
          },
        ],
      ),

      // 4. AI personalized content generator
      aiNode(
        "4",
        "AI Generate Personalized Follow-Up Content",
        760,
        "You are a post-purchase marketing specialist. Create personalized follow-up content for three touchpoints: a thank-you message (warm, appreciative), a review request (encouraging, easy), and a cross-sell recommendation (relevant, non-pushy). Base recommendations on the products purchased and customer history. Respond JSON: {thank_you_message: string, review_request_message: string, cross_sell_message: string, recommended_products: string, review_url: string, loyalty_points_earned: string}",
        "Customer: {customer_name} ({customer_email}). Order #{order_id}: {product_names} (${order_total}). Delivered: {delivery_date}. Total past orders: {total_orders}. Lifecycle: {lifecycle_stage}. Generate three personalized messages.",
        [
          {
            field: "thank_you_message",
            as: "thank_you_message",
            type: "string",
          },
          {
            field: "review_request_message",
            as: "review_request_message",
            type: "string",
          },
          {
            field: "cross_sell_message",
            as: "cross_sell_message",
            type: "string",
          },
          {
            field: "recommended_products",
            as: "recommended_products",
            type: "string",
          },
          { field: "review_url", as: "review_url", type: "string" },
          {
            field: "loyalty_points_earned",
            as: "loyalty_points_earned",
            type: "string",
          },
        ],
      ),

      // 5. Day 3 thank you email
      emailNode(
        "5",
        "Send Day 3 Thank You Email",
        1000,
        "{customer_email}",
        "Thank you for your order, {customer_name}!",
        "<h2>Thank You for Your Purchase!</h2><p>Hi {customer_name},</p>{thank_you_message}<p><strong>Order #{order_id}</strong></p><ul><li>Items: {product_names}</li><li>Total: ${order_total}</li><li>Delivered: {delivery_date} via {shipping_carrier}</li></ul><p>You earned {loyalty_points_earned} loyalty points with this purchase!</p><p>If anything isn't perfect with your order, our support team is here to help.</p>",
      ),

      // 6. Delay 4 days
      n("6", "delayAction", "Wait 4 Days Before Review Request", 1240, {
        delayDetails: { duration: 4, unit: "days" },
      }),

      // 7. Day 7 review request email
      emailNode(
        "7",
        "Send Day 7 Review Request Email",
        1480,
        "{customer_email}",
        "How are you enjoying your {product_names}, {customer_name}?",
        "<h2>We'd Love Your Feedback!</h2><p>Hi {customer_name},</p>{review_request_message}<p>Your review of <strong>{product_names}</strong> helps other shoppers make informed decisions and helps us improve.</p><p><a href='{review_url}'>Leave a Review</a></p><p>It only takes 2 minutes, and we truly appreciate every piece of feedback.</p>",
      ),

      // 8. Day 14 cross-sell email
      emailNode(
        "8",
        "Send Day 14 Cross-Sell Email",
        1720,
        "{customer_email}",
        "Picked just for you, {customer_name} — based on your recent purchase",
        "<h2>You Might Also Love These</h2><p>Hi {customer_name},</p>{cross_sell_message}<p>Based on your purchase of {product_names}, we thought you'd be interested in:</p><p><strong>{recommended_products}</strong></p><p>As a valued customer with {total_orders} orders, you get early access to new arrivals and exclusive offers.</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1391. POST PURCHASE REVIEW REQUEST (7 nodes)
  // ===================================================================

  "Post Purchase Review Request": {
    summary:
      "Triggers on delivery confirmation webhooks, extracts order data with AI, optimizes review request timing with runCode, composes a personalized review request with AI, sends the review request email, waits 7 days for a response, and checks whether a review was submitted via a condition node.",
    nodes: [
      trigger("Webhook: Order delivered confirmed", "webhook_app", {
        source: "fulfillment_system",
        event: "order.delivered",
        howItStarts:
          "Fires when the fulfillment system confirms an order has been delivered, including order ID, items, customer details, and delivery confirmation timestamp",
        setup:
          "Configure webhook from your fulfillment or shipping system. Set OpenAI API key for personalized content. Configure email service for review requests. Set review platform URL.",
        inputFields: [],
      }),

      // 2. AI extract order data
      aiNode(
        "2",
        "AI Extract Delivered Order Details",
        280,
        "You are an order data parser. Extract the order information from the delivery confirmation webhook payload, including order ID, customer email, customer first name, product name, product category, order value, and delivery date. Respond JSON: {order_id: string, customer_email: string, customer_first_name: string, product_name: string, product_category: string, order_value: string, delivery_date: string}",
        "Raw webhook payload: {webhook_payload}. Extract order and customer details regardless of field naming.",
        [
          { field: "order_id", as: "order_id", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          {
            field: "customer_first_name",
            as: "customer_first_name",
            type: "string",
          },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_category", as: "product_category", type: "string" },
          { field: "order_value", as: "order_value", type: "string" },
          { field: "delivery_date", as: "delivery_date", type: "string" },
        ],
      ),

      // 3. Timing optimizer runCode
      n("3", "runCode", "Optimize Review Request Timing", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine optimal timing based on product category and order value
const category = (vars.product_category || "general").toLowerCase();
const orderValue = parseFloat(vars.order_value || "50");
const deliveryDate = new Date(vars.delivery_date || new Date().toISOString());
// Higher-value or complex products need more usage time
let optimalDays = 3;
if (category.includes("electronics") || category.includes("tech")) optimalDays = 5;
else if (category.includes("furniture") || category.includes("appliance")) optimalDays = 7;
else if (category.includes("clothing") || category.includes("fashion")) optimalDays = 2;
else if (orderValue > 200) optimalDays = 5;
const sendDate = new Date(deliveryDate.getTime() + optimalDays * 24 * 60 * 60 * 1000);
const incentive = orderValue > 100 ? "10% off your next order" : "free shipping on your next order";
return {
  optimal_delay_days: optimalDays,
  scheduled_send_date: sendDate.toISOString().split("T")[0],
  review_incentive: incentive,
  product_usage_time: optimalDays + " days",
  review_platform_url: "https://reviews.company.com/submit?order=" + (vars.order_id || ""),
  timing_reason: category + " products typically need " + optimalDays + " days of use before review"
};`,
          outputVariables: [
            { name: "optimal_delay_days", type: "number" },
            { name: "scheduled_send_date", type: "string" },
            { name: "review_incentive", type: "string" },
            { name: "product_usage_time", type: "string" },
            { name: "review_platform_url", type: "string" },
            { name: "timing_reason", type: "string" },
          ],
        },
      }),

      // 4. AI review request composer
      aiNode(
        "4",
        "AI Compose Personalized Review Request",
        760,
        "You are a customer engagement writer. Create a warm, genuine review request email that feels personal, not automated. Reference the specific product, acknowledge the usage time, offer the incentive naturally, and make leaving a review feel easy and valuable. Respond JSON: {subject_line: string, email_body: string, cta_text: string, tone: string}",
        "Customer: {customer_first_name}. Product: {product_name} (Category: {product_category}). Order value: ${order_value}. Delivered: {delivery_date}. Usage time: {product_usage_time}. Incentive: {review_incentive}. Review URL: {review_platform_url}. Compose a personalized, non-generic review request.",
        [
          { field: "subject_line", as: "review_subject", type: "string" },
          { field: "email_body", as: "review_email_body", type: "string" },
          { field: "cta_text", as: "review_cta_text", type: "string" },
          { field: "tone", as: "email_tone", type: "string" },
        ],
      ),

      // 5. Review request email
      emailNode(
        "5",
        "Send Review Request Email",
        1000,
        "{customer_email}",
        "{review_subject}",
        "{review_email_body}<p><a href='{review_platform_url}'>{review_cta_text}</a></p><p>As a thank-you, you'll receive <strong>{review_incentive}</strong> after submitting your review.</p>",
      ),

      // 6. Delay 7 days
      n("6", "delayAction", "Wait 7 Days for Review Submission", 1240, {
        delayDetails: { duration: 7, unit: "days" },
      }),

      // 7. Response handler condition
      n("7", "condition", "Review Submitted Within Window?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "review_platform_url", operator: "neq", value: "" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1392. PRICING CALCULATOR CHATBOT (8 nodes)
  // ===================================================================

  "Pricing Calculator Chatbot": {
    summary:
      "Receives pricing questions via webhook, qualifies the lead with AI, queries the pricing engine with runCode, generates a dynamic quote presentation with AI, handles objections with AI, books a demo via Calendly API, captures the lead in HubSpot CRM, and notifies the sales rep via Slack.",
    nodes: [
      trigger("Webhook: Pricing question received", "webhook_app", {
        source: "website_chat",
        event: "chat.pricing_inquiry",
        howItStarts:
          "Fires when a visitor asks a pricing-related question through the website chat widget, pricing page form, or API integration",
        setup:
          "Configure webhook from your chat widget or pricing page. Set OpenAI API key for conversational AI. Configure Calendly API for demo booking. Set HubSpot API for CRM lead capture. Set Slack webhook for sales notifications.",
        inputFields: [],
      }),

      // 2. AI qualification engine
      aiNode(
        "2",
        "AI Qualify Lead and Parse Pricing Query",
        280,
        "You are a sales qualification bot for a SaaS product. Parse the visitor's pricing inquiry to determine: what product/plan they're interested in, their company size, use case, budget signals, and qualification level (hot/warm/cold). Respond JSON: {visitor_email: string, visitor_name: string, company_name: string, company_size: string, product_interest: string, use_case: string, budget_range: string, qualification_level: string, pricing_question: string}",
        "Raw webhook payload: {webhook_payload}. Qualify the lead and extract all pricing inquiry details regardless of format.",
        [
          { field: "visitor_email", as: "visitor_email", type: "string" },
          { field: "visitor_name", as: "visitor_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "company_size", as: "company_size", type: "string" },
          { field: "product_interest", as: "product_interest", type: "string" },
          { field: "use_case", as: "use_case", type: "string" },
          { field: "budget_range", as: "budget_range", type: "string" },
          {
            field: "qualification_level",
            as: "qualification_level",
            type: "string",
          },
          { field: "pricing_question", as: "pricing_question", type: "string" },
        ],
      ),

      // 3. Pricing engine query runCode
      n("3", "runCode", "Calculate Dynamic Pricing Quote", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Dynamic pricing engine based on company size and product interest
const product = (vars.product_interest || "starter").toLowerCase();
const size = (vars.company_size || "1-10").toLowerCase();
const plans = {
  starter: { base: 29, perUser: 5, features: "Core features, email support, 5 integrations" },
  professional: { base: 79, perUser: 10, features: "All Starter + advanced analytics, priority support, 25 integrations, API access" },
  enterprise: { base: 199, perUser: 15, features: "All Professional + SSO, dedicated CSM, unlimited integrations, custom SLA, audit logs" }
};
const sizeMap = { "1-10": 5, "11-50": 25, "51-200": 100, "201-500": 300, "500+": 500 };
const userCount = sizeMap[size] || 10;
const plan = plans[product] || plans.starter;
const monthlyPrice = plan.base + (plan.perUser * userCount);
const annualPrice = Math.round(monthlyPrice * 12 * 0.8);
const annualSavings = (monthlyPrice * 12) - annualPrice;
const volumeDiscount = userCount > 100 ? "15% volume discount available" : userCount > 50 ? "10% volume discount available" : "Standard pricing";
return {
  recommended_plan: product,
  monthly_price: monthlyPrice,
  annual_price: annualPrice,
  annual_savings: annualSavings,
  estimated_users: userCount,
  plan_features: plan.features,
  volume_discount: volumeDiscount,
  price_per_user: plan.perUser,
  quote_id: "Q-" + Date.now().toString(36).toUpperCase()
};`,
          outputVariables: [
            { name: "recommended_plan", type: "string" },
            { name: "monthly_price", type: "number" },
            { name: "annual_price", type: "number" },
            { name: "annual_savings", type: "number" },
            { name: "estimated_users", type: "number" },
            { name: "plan_features", type: "string" },
            { name: "volume_discount", type: "string" },
            { name: "price_per_user", type: "number" },
            { name: "quote_id", type: "string" },
          ],
        },
      }),

      // 4. AI dynamic quote presenter
      aiNode(
        "4",
        "AI Present Dynamic Quote with Context",
        760,
        "You are a consultative sales assistant. Present the pricing quote in a conversational, helpful way. Address the visitor's specific use case, highlight relevant features, explain the value proposition, and proactively address common pricing concerns. Respond JSON: {quote_presentation: string, value_highlights: string, comparison_note: string, next_step_suggestion: string}",
        "Visitor: {visitor_name} from {company_name} ({company_size} employees). Use case: {use_case}. Question: {pricing_question}. Quote: {recommended_plan} plan at ${monthly_price}/mo (${annual_price}/yr, saving ${annual_savings}). {estimated_users} users at ${price_per_user}/user. Features: {plan_features}. {volume_discount}. Quote ID: {quote_id}. Present this quote conversationally.",
        [
          {
            field: "quote_presentation",
            as: "quote_presentation",
            type: "string",
          },
          { field: "value_highlights", as: "value_highlights", type: "string" },
          { field: "comparison_note", as: "comparison_note", type: "string" },
          {
            field: "next_step_suggestion",
            as: "next_step_suggestion",
            type: "string",
          },
        ],
      ),

      // 5. Objection handler AI
      aiNode(
        "5",
        "AI Handle Pricing Objections",
        1000,
        "You are a pricing objection specialist. Anticipate and prepare responses for the most likely objections this prospect might raise based on their company size, budget signals, and use case. Provide ROI justification and competitor comparison talking points. Respond JSON: {likely_objections: string, roi_justification: string, competitor_comparison: string, negotiation_flexibility: string, urgency_driver: string}",
        "Prospect: {company_name}, {company_size} employees. Budget range: {budget_range}. Qualification: {qualification_level}. Quoted: ${monthly_price}/mo for {recommended_plan} plan. Use case: {use_case}. Prepare objection handling responses.",
        [
          {
            field: "likely_objections",
            as: "likely_objections",
            type: "string",
          },
          {
            field: "roi_justification",
            as: "roi_justification",
            type: "string",
          },
          {
            field: "competitor_comparison",
            as: "competitor_comparison",
            type: "string",
          },
          {
            field: "negotiation_flexibility",
            as: "negotiation_flexibility",
            type: "string",
          },
          { field: "urgency_driver", as: "urgency_driver", type: "string" },
        ],
      ),

      // 6. Demo booking Calendly API
      n("6", "hitApi", "Create Demo Booking via Calendly", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.calendly.com/scheduling_links",
          headers: [
            { key: "Authorization", value: "Bearer {calendly_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "max_event_count", value: "1" },
            {
              key: "owner",
              value:
                "https://api.calendly.com/event_types/{demo_event_type_uuid}",
            },
            { key: "owner_type", value: "EventType" },
          ],
          variables: [
            {
              name: "resource.booking_url",
              customName: "demo_booking_url",
              type: "string",
            },
            {
              name: "resource.owner",
              customName: "calendly_event_owner",
              type: "string",
            },
          ],
        },
      }),

      // 7. CRM lead capture HubSpot
      hubspotPostNode(
        "7",
        "Capture Lead in HubSpot CRM",
        1480,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{visitor_email}","firstname":"{visitor_name}","company":"{company_name}","jobtitle":"Pricing Inquiry","hs_lead_status":"NEW","num_employees":"{company_size}","notes_last_activity":"Quote {quote_id}: {recommended_plan} plan ${monthly_price}/mo. Use case: {use_case}. Qualification: {qualification_level}"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "hs_contact_email",
            type: "string",
          },
        ],
      ),

      // 8. Sales rep handoff Slack
      slackNode(
        "8",
        "Notify Sales Rep of Qualified Lead",
        1720,
        "New Pricing Inquiry from {visitor_name} ({company_name})\\n*Qualification:* {qualification_level}\\n*Quote:* {recommended_plan} plan — ${monthly_price}/mo (${annual_price}/yr)\\n*Company Size:* {company_size} | *Use Case:* {use_case}\\n*Budget Range:* {budget_range}\\n*Quote ID:* {quote_id}\\n*Demo Link:* {demo_booking_url}\\n*HubSpot:* Contact #{hubspot_contact_id}\\n*Objection Prep:* {likely_objections}\\n*ROI Points:* {roi_justification}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1393. PROCUREMENT RFP DISTRIBUTOR (8 nodes)
  // ===================================================================

  "Procurement RFP Distributor": {
    summary:
      "Manually triggered when an RFP is approved, resolves the vendor list from HubSpot, personalizes RFP packages with AI, distributes RFP emails to vendors, waits 14 days for submissions, checks submission tracking via a condition, sends Q&A follow-up emails, and assembles the evaluation package in Google Sheets.",
    nodes: [
      trigger("Manual: RFP approved for distribution", "manual", {
        howItStarts:
          "Manually triggered when a procurement RFP has been internally approved and is ready for distribution to qualified vendors, including RFP title, scope, budget range, and submission deadline",
        setup:
          "Configure HubSpot API for vendor contact lists. Set OpenAI API key for personalization. Configure email service for RFP distribution. Set Google Sheets for evaluation tracking.",
        inputFields: [
          {
            name: "rfp_title",
            type: "string",
            example: "Cloud Infrastructure Migration Services",
          },
          { name: "rfp_number", type: "string", example: "RFP-2026-0042" },
          {
            name: "rfp_scope",
            type: "string",
            example: "Migration of on-premise infrastructure to AWS/Azure",
          },
          { name: "budget_range", type: "string", example: "$500K - $1.2M" },
          {
            name: "submission_deadline",
            type: "string",
            example: "2026-05-01",
          },
          {
            name: "vendor_list_id",
            type: "string",
            example: "qualified-cloud-vendors",
          },
          {
            name: "procurement_email",
            type: "string",
            example: "procurement@company.com",
          },
        ],
      }),

      // 2. Vendor list resolver HubSpot
      hubspotGetNode(
        "2",
        "Fetch Qualified Vendor List from HubSpot",
        280,
        "crm/v3/objects/companies/search",
        [
          { name: "results", customName: "vendor_results", type: "string" },
          { name: "total", customName: "total_vendors", type: "number" },
        ],
      ),

      // 3. AI RFP personalization
      aiNode(
        "3",
        "AI Personalize RFP Distribution Packages",
        520,
        "You are a procurement specialist. Create a professional RFP cover letter and executive summary tailored for vendor distribution. Include clear instructions for submission, evaluation criteria, timeline, and Q&A process. Respond JSON: {cover_letter: string, executive_summary: string, evaluation_criteria: string, submission_instructions: string, qa_deadline: string, key_requirements: string, disqualification_criteria: string}",
        "RFP: {rfp_title} ({rfp_number}). Scope: {rfp_scope}. Budget: {budget_range}. Submission deadline: {submission_deadline}. Total vendors to receive: {total_vendors}. Create professional RFP distribution materials.",
        [
          { field: "cover_letter", as: "rfp_cover_letter", type: "string" },
          {
            field: "executive_summary",
            as: "rfp_executive_summary",
            type: "string",
          },
          {
            field: "evaluation_criteria",
            as: "evaluation_criteria",
            type: "string",
          },
          {
            field: "submission_instructions",
            as: "submission_instructions",
            type: "string",
          },
          { field: "qa_deadline", as: "qa_deadline", type: "string" },
          { field: "key_requirements", as: "key_requirements", type: "string" },
          {
            field: "disqualification_criteria",
            as: "disqualification_criteria",
            type: "string",
          },
        ],
      ),

      // 4. RFP distribution email
      emailNode(
        "4",
        "Distribute RFP to Qualified Vendors",
        760,
        "{procurement_email}",
        "Request for Proposal: {rfp_title} ({rfp_number}) — Submission Deadline: {submission_deadline}",
        "<h2>Request for Proposal</h2><p><strong>{rfp_title}</strong> — {rfp_number}</p>{rfp_cover_letter}<hr><h3>Executive Summary</h3>{rfp_executive_summary}<h3>Key Requirements</h3><p>{key_requirements}</p><h3>Evaluation Criteria</h3><p>{evaluation_criteria}</p><h3>Submission Instructions</h3><p>{submission_instructions}</p><h3>Important Dates</h3><ul><li>Q&A Deadline: {qa_deadline}</li><li>Submission Deadline: {submission_deadline}</li><li>Budget Range: {budget_range}</li></ul><h3>Disqualification Criteria</h3><p>{disqualification_criteria}</p>",
      ),

      // 5. Delay 14 days
      n("5", "delayAction", "Wait 14 Days for Vendor Submissions", 1000, {
        delayDetails: { duration: 14, unit: "days" },
      }),

      // 6. Submission tracking condition
      n("6", "condition", "Sufficient Submissions Received?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "total_vendors", operator: "gte", value: "3" },
          ],
        },
      }),

      // 7. Q&A manager email (False path — not enough submissions)
      emailNode(
        "7",
        "Send Q&A Follow-Up to Non-Responding Vendors",
        1480,
        "{procurement_email}",
        "Reminder: {rfp_title} ({rfp_number}) — Submission Deadline Approaching",
        "<h2>RFP Submission Reminder</h2><p>This is a reminder that the submission deadline for <strong>{rfp_title}</strong> ({rfp_number}) is approaching on <strong>{submission_deadline}</strong>.</p><p>If you have any questions about the requirements or submission process, please contact us at {procurement_email} before the Q&A deadline ({qa_deadline}).</p><h3>Key Requirements Recap</h3><p>{key_requirements}</p><p>{submission_instructions}</p>",
        400,
      ),

      // 8. Evaluation package assembly Sheets
      sheetsNode(
        "8",
        "Log RFP Distribution to Evaluation Tracker",
        1720,
        "RFPTracker",
        '[["{rfp_number}","{rfp_title}","{rfp_scope}","{budget_range}","{submission_deadline}","{total_vendors}","{evaluation_criteria}","{qa_deadline}","DISTRIBUTED"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "8", "output_1"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1394. PROJECT CLOSURE CHECKLIST (8 nodes)
  // ===================================================================

  "Project Closure Checklist": {
    summary:
      "Triggers on project closure webhooks, extracts project data with AI, generates a closure checklist with runCode, creates task assignments via Asana API, routes deliverable sign-off through an approval gate, performs financial reconciliation with runCode, validates documentation completeness via a condition, and logs the final closure status to Google Sheets.",
    nodes: [
      trigger("Webhook: Project closure initiated", "webhook_app", {
        source: "project_management",
        event: "project.closure_initiated",
        howItStarts:
          "Fires when a project manager initiates the formal project closure process, including project ID, name, client, budget, timeline, and deliverables list",
        setup:
          "Configure webhook from your project management tool (Jira, Asana, Monday.com). Set OpenAI API key for AI extraction. Configure Asana API for task creation. Set Google Sheets for closure tracking.",
        inputFields: [],
      }),

      // 2. AI extract project data
      aiNode(
        "2",
        "AI Extract Project Closure Data",
        280,
        "You are a project management assistant. Extract the project closure details from the webhook payload including project ID, name, client, PM email, original budget, actual spend, start date, end date, deliverables list, and team members. Respond JSON: {project_id: string, project_name: string, client_name: string, pm_email: string, original_budget: string, actual_spend: string, start_date: string, end_date: string, deliverables: string, team_members: string, project_status: string}",
        "Raw webhook payload: {webhook_payload}. Extract all project closure details regardless of payload format.",
        [
          { field: "project_id", as: "project_id", type: "string" },
          { field: "project_name", as: "project_name", type: "string" },
          { field: "client_name", as: "client_name", type: "string" },
          { field: "pm_email", as: "pm_email", type: "string" },
          { field: "original_budget", as: "original_budget", type: "string" },
          { field: "actual_spend", as: "actual_spend", type: "string" },
          { field: "start_date", as: "start_date", type: "string" },
          { field: "end_date", as: "end_date", type: "string" },
          { field: "deliverables", as: "deliverables", type: "string" },
          { field: "team_members", as: "team_members", type: "string" },
          { field: "project_status", as: "project_status", type: "string" },
        ],
      ),

      // 3. Checklist generator runCode
      n("3", "runCode", "Generate Project Closure Checklist", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate a comprehensive project closure checklist
const projectName = vars.project_name || "Unknown Project";
const deliverables = (vars.deliverables || "").split(",").map(d => d.trim()).filter(Boolean);
const checklistItems = [
  "Final deliverable review and client acceptance for: " + deliverables.join(", "),
  "Complete all outstanding change requests and defect fixes",
  "Conduct lessons learned session with project team",
  "Archive all project documentation in knowledge base",
  "Close all vendor contracts and purchase orders",
  "Release project resources and update resource allocation",
  "Collect final client satisfaction survey",
  "Perform financial reconciliation — budget vs actual",
  "Update project portfolio dashboard",
  "Send project completion notification to stakeholders",
  "Transfer support ownership to operations team",
  "Schedule 30-day post-closure review"
];
return {
  checklist_items: checklistItems.join(" | "),
  total_checklist_items: checklistItems.length,
  deliverable_count: deliverables.length,
  closure_initiated_date: new Date().toISOString().split("T")[0],
  target_closure_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
  checklist_status: "IN_PROGRESS"
};`,
          outputVariables: [
            { name: "checklist_items", type: "string" },
            { name: "total_checklist_items", type: "number" },
            { name: "deliverable_count", type: "number" },
            { name: "closure_initiated_date", type: "string" },
            { name: "target_closure_date", type: "string" },
            { name: "checklist_status", type: "string" },
          ],
        },
      }),

      // 4. Task assignment Asana API
      n("4", "hitApi", "Create Closure Tasks in Asana", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"Project Closure: {project_name}","notes":"Checklist items: {checklist_items}","projects":["{asana_project_id}"],"due_on":"{target_closure_date}","assignee":"{pm_email}"}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "asana_task_id", type: "string" },
            {
              name: "data.permalink_url",
              customName: "asana_task_url",
              type: "string",
            },
          ],
        },
      }),

      // 5. Deliverable sign-off approval gate
      n("5", "approvalGate", "Deliverable Sign-Off Approval", 1000, {
        approvalGateDetails: {
          approverEmail: "{pm_email}",
          approvalMessage:
            "Project Closure Sign-Off Required\\n\\nProject: {project_name} ({project_id})\\nClient: {client_name}\\nTimeline: {start_date} to {end_date}\\n\\nDeliverables ({deliverable_count}): {deliverables}\\nChecklist Items: {total_checklist_items}\\nTarget Closure Date: {target_closure_date}\\n\\nAsana Task: {asana_task_url}\\n\\nConfirm all deliverables have been accepted by the client and the closure checklist is progressing.",
        },
      }),

      // 6. Financial reconciliation runCode
      n("6", "runCode", "Perform Financial Reconciliation", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate budget vs actual financial reconciliation
const originalBudget = parseFloat((vars.original_budget || "0").replace(/[^0-9.]/g, ""));
const actualSpend = parseFloat((vars.actual_spend || "0").replace(/[^0-9.]/g, ""));
const variance = originalBudget - actualSpend;
const variancePercent = originalBudget > 0 ? Math.round((variance / originalBudget) * 100) : 0;
const budgetStatus = variance >= 0 ? "UNDER_BUDGET" : "OVER_BUDGET";
const profitability = variance >= 0 ? "Profitable" : "Loss";
const overrunAmount = variance < 0 ? Math.abs(variance) : 0;
const savingsAmount = variance > 0 ? variance : 0;
return {
  budget_variance: variance,
  variance_percent: variancePercent,
  budget_status: budgetStatus,
  profitability_status: profitability,
  overrun_amount: overrunAmount,
  savings_amount: savingsAmount,
  reconciliation_date: new Date().toISOString().split("T")[0],
  financial_summary: "Budget: $" + originalBudget.toLocaleString() + " | Actual: $" + actualSpend.toLocaleString() + " | Variance: $" + variance.toLocaleString() + " (" + variancePercent + "%) — " + budgetStatus
};`,
          outputVariables: [
            { name: "budget_variance", type: "number" },
            { name: "variance_percent", type: "number" },
            { name: "budget_status", type: "string" },
            { name: "profitability_status", type: "string" },
            { name: "overrun_amount", type: "number" },
            { name: "savings_amount", type: "number" },
            { name: "reconciliation_date", type: "string" },
            { name: "financial_summary", type: "string" },
          ],
        },
      }),

      // 7. Documentation verification condition
      n(
        "7",
        "condition",
        "Financial Reconciliation Within Acceptable Range?",
        1480,
        {
          conditionDetails: {
            conditions: [
              { variable: "variance_percent", operator: "gte", value: "-10" },
            ],
          },
        },
      ),

      // 8. Closure completion Sheets
      sheetsNode(
        "8",
        "Log Project Closure to Sheets",
        1720,
        "ProjectClosures",
        '[["{closure_initiated_date}","{project_id}","{project_name}","{client_name}","{original_budget}","{actual_spend}","{budget_variance}","{variance_percent}%","{budget_status}","{deliverable_count}","{total_checklist_items}","{checklist_status}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1395. PROJECT HANDOFF CHECKLIST BOT (8 nodes)
  // ===================================================================

  "Project Handoff Checklist Bot": {
    summary:
      "Triggers on handoff request webhooks, extracts handoff details with AI, generates a comprehensive handoff checklist with AI, inventories knowledge base assets with runCode, audits access transfers with runCode, creates tracking tasks in Asana, schedules knowledge transfer sessions via Google Calendar API, and validates handoff completion with a condition.",
    nodes: [
      trigger("Webhook: Project handoff request", "webhook_app", {
        source: "project_management",
        event: "project.handoff_requested",
        howItStarts:
          "Fires when a project handoff is requested between teams, individuals, or departments, including the project details, outgoing owner, incoming owner, and handoff timeline",
        setup:
          "Configure webhook from your project management tool. Set OpenAI API key for AI analysis. Configure Asana API for task tracking. Set Google Calendar API for KT scheduling.",
        inputFields: [],
      }),

      // 2. AI extract handoff data
      aiNode(
        "2",
        "AI Extract Handoff Request Details",
        280,
        "You are a project handoff coordinator. Extract all handoff details from the webhook payload including project name, outgoing owner email, incoming owner email, handoff reason, deadline, critical systems, documentation links, and stakeholder contacts. Respond JSON: {project_name: string, outgoing_owner_email: string, outgoing_owner_name: string, incoming_owner_email: string, incoming_owner_name: string, handoff_reason: string, handoff_deadline: string, critical_systems: string, documentation_links: string, stakeholder_contacts: string}",
        "Raw webhook payload: {webhook_payload}. Extract all handoff details regardless of format.",
        [
          { field: "project_name", as: "project_name", type: "string" },
          {
            field: "outgoing_owner_email",
            as: "outgoing_owner_email",
            type: "string",
          },
          {
            field: "outgoing_owner_name",
            as: "outgoing_owner_name",
            type: "string",
          },
          {
            field: "incoming_owner_email",
            as: "incoming_owner_email",
            type: "string",
          },
          {
            field: "incoming_owner_name",
            as: "incoming_owner_name",
            type: "string",
          },
          { field: "handoff_reason", as: "handoff_reason", type: "string" },
          { field: "handoff_deadline", as: "handoff_deadline", type: "string" },
          { field: "critical_systems", as: "critical_systems", type: "string" },
          {
            field: "documentation_links",
            as: "documentation_links",
            type: "string",
          },
          {
            field: "stakeholder_contacts",
            as: "stakeholder_contacts",
            type: "string",
          },
        ],
      ),

      // 3. AI checklist generator
      aiNode(
        "3",
        "AI Generate Comprehensive Handoff Checklist",
        520,
        "You are a handoff best-practices expert. Generate a detailed handoff checklist covering: documentation transfer, access provisioning, stakeholder introductions, knowledge transfer sessions, pending items, risk areas, escalation paths, and post-handoff support plan. Respond JSON: {checklist_items: string, total_items: number, priority_items: string, risk_areas: string, estimated_days: number, kt_topics: string, pending_decisions: string}",
        "Project: {project_name}. Outgoing: {outgoing_owner_name}. Incoming: {incoming_owner_name}. Reason: {handoff_reason}. Deadline: {handoff_deadline}. Critical systems: {critical_systems}. Documentation: {documentation_links}. Stakeholders: {stakeholder_contacts}. Generate a thorough handoff checklist.",
        [
          { field: "checklist_items", as: "checklist_items", type: "string" },
          { field: "total_items", as: "total_checklist_items", type: "number" },
          { field: "priority_items", as: "priority_items", type: "string" },
          { field: "risk_areas", as: "risk_areas", type: "string" },
          {
            field: "estimated_days",
            as: "estimated_handoff_days",
            type: "number",
          },
          { field: "kt_topics", as: "kt_topics", type: "string" },
          {
            field: "pending_decisions",
            as: "pending_decisions",
            type: "string",
          },
        ],
      ),

      // 4. Knowledge base inventory runCode
      n("4", "runCode", "Inventory Knowledge Base Assets", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Inventory documentation and knowledge assets for handoff
const docLinks = (vars.documentation_links || "").split(",").map(d => d.trim()).filter(Boolean);
const criticalSystems = (vars.critical_systems || "").split(",").map(s => s.trim()).filter(Boolean);
const ktTopics = (vars.kt_topics || "").split(",").map(t => t.trim()).filter(Boolean);
const totalAssets = docLinks.length + criticalSystems.length;
const assetInventory = [];
docLinks.forEach((doc, i) => assetInventory.push("DOC-" + (i+1) + ": " + doc));
criticalSystems.forEach((sys, i) => assetInventory.push("SYS-" + (i+1) + ": " + sys));
const completeness = totalAssets >= 5 ? "comprehensive" : totalAssets >= 3 ? "adequate" : "needs_supplementation";
return {
  total_assets: totalAssets,
  document_count: docLinks.length,
  system_count: criticalSystems.length,
  asset_inventory: assetInventory.join(" | ") || "No assets cataloged",
  kt_session_count: ktTopics.length,
  inventory_completeness: completeness,
  handoff_readiness: completeness === "needs_supplementation" ? "not_ready" : "ready"
};`,
          outputVariables: [
            { name: "total_assets", type: "number" },
            { name: "document_count", type: "number" },
            { name: "system_count", type: "number" },
            { name: "asset_inventory", type: "string" },
            { name: "kt_session_count", type: "number" },
            { name: "inventory_completeness", type: "string" },
            { name: "handoff_readiness", type: "string" },
          ],
        },
      }),

      // 5. Access transfer auditor runCode
      n("5", "runCode", "Audit Access Transfer Requirements", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate access transfer audit and provisioning plan
const systems = (vars.critical_systems || "").split(",").map(s => s.trim()).filter(Boolean);
const incomingOwner = vars.incoming_owner_name || "New Owner";
const outgoingOwner = vars.outgoing_owner_name || "Previous Owner";
const accessItems = systems.map(sys => "Grant " + incomingOwner + " access to " + sys + " → Revoke " + outgoingOwner + " after transition");
const totalTransfers = accessItems.length;
const accessPlan = accessItems.join(" | ") || "No system access transfers required";
const securityNote = totalTransfers > 5 ? "HIGH — multiple system accesses, requires IT coordination" : totalTransfers > 2 ? "MEDIUM — standard access transfer batch" : "LOW — minimal access changes";
return {
  access_transfer_plan: accessPlan,
  total_access_transfers: totalTransfers,
  security_risk_level: securityNote,
  it_ticket_required: totalTransfers > 3 ? "yes" : "no",
  estimated_transfer_hours: Math.max(1, totalTransfers * 0.5)
};`,
          outputVariables: [
            { name: "access_transfer_plan", type: "string" },
            { name: "total_access_transfers", type: "number" },
            { name: "security_risk_level", type: "string" },
            { name: "it_ticket_required", type: "string" },
            { name: "estimated_transfer_hours", type: "number" },
          ],
        },
      }),

      // 6. Task tracker creation Asana API
      n("6", "hitApi", "Create Handoff Tracking Tasks in Asana", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"Handoff: {project_name} — {outgoing_owner_name} to {incoming_owner_name}","notes":"Checklist: {checklist_items}\\n\\nPriority Items: {priority_items}\\nRisk Areas: {risk_areas}\\nAccess Transfers: {access_transfer_plan}\\nAssets: {asset_inventory}","projects":["{asana_project_id}"],"due_on":"{handoff_deadline}","assignee":"{incoming_owner_email}"}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "handoff_task_id", type: "string" },
            {
              name: "data.permalink_url",
              customName: "handoff_task_url",
              type: "string",
            },
          ],
        },
      }),

      // 7. KT session scheduler Google Calendar API
      n("7", "hitApi", "Schedule Knowledge Transfer Sessions", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/calendars/primary/events",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "summary", value: "KT Session: {project_name} Handoff" },
            {
              key: "description",
              value:
                "Knowledge transfer from {outgoing_owner_name} to {incoming_owner_name}.\\nTopics: {kt_topics}\\nPending Decisions: {pending_decisions}\\nTask: {handoff_task_url}",
            },
            {
              key: "start",
              value:
                '{"dateTime":"2026-04-01T10:00:00-05:00","timeZone":"America/Chicago"}',
            },
            {
              key: "end",
              value:
                '{"dateTime":"2026-04-01T11:30:00-05:00","timeZone":"America/Chicago"}',
            },
            {
              key: "attendees",
              value:
                '[{"email":"{outgoing_owner_email}"},{"email":"{incoming_owner_email}"}]',
            },
          ],
          variables: [
            { name: "id", customName: "calendar_event_id", type: "string" },
            {
              name: "htmlLink",
              customName: "calendar_event_link",
              type: "string",
            },
          ],
        },
      }),

      // 8. Handoff completion condition
      n("8", "condition", "Handoff Readiness Verified?", 1720, {
        conditionDetails: {
          conditions: [
            { variable: "handoff_readiness", operator: "eq", value: "ready" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1396. PROJECT KICKOFF CHECKLIST (9 nodes)
  // ===================================================================

  "Project Kickoff Checklist": {
    summary:
      "Triggers on project approval webhooks, extracts project data with AI, selects a checklist template with runCode, generates tasks in Asana, sets up a Slack channel workspace, sends stakeholder notification emails, schedules a kickoff meeting via Google Calendar API, provisions tools via API, and initializes a risk register in Google Sheets.",
    nodes: [
      trigger("Webhook: Project approved for kickoff", "webhook_app", {
        source: "project_management",
        event: "project.approved",
        howItStarts:
          "Fires when a new project receives final approval and is ready for kickoff, including project details, budget, timeline, team assignments, and stakeholder list",
        setup:
          "Configure webhook from your project approval system. Set OpenAI API key. Configure Asana API for task creation. Set Slack API for channel setup. Configure Google Calendar and Sheets APIs.",
        inputFields: [],
      }),

      // 2. AI extract project data
      aiNode(
        "2",
        "AI Extract Approved Project Details",
        280,
        "You are a project kickoff coordinator. Extract all project details from the approval webhook including project name, project ID, PM email, sponsor name, budget, start date, end date, team members, key deliverables, and project type. Respond JSON: {project_name: string, project_id: string, pm_email: string, sponsor_name: string, sponsor_email: string, budget: string, start_date: string, end_date: string, team_members: string, key_deliverables: string, project_type: string}",
        "Raw webhook payload: {webhook_payload}. Extract all approved project details regardless of field naming.",
        [
          { field: "project_name", as: "project_name", type: "string" },
          { field: "project_id", as: "project_id", type: "string" },
          { field: "pm_email", as: "pm_email", type: "string" },
          { field: "sponsor_name", as: "sponsor_name", type: "string" },
          { field: "sponsor_email", as: "sponsor_email", type: "string" },
          { field: "budget", as: "budget", type: "string" },
          { field: "start_date", as: "start_date", type: "string" },
          { field: "end_date", as: "end_date", type: "string" },
          { field: "team_members", as: "team_members", type: "string" },
          { field: "key_deliverables", as: "key_deliverables", type: "string" },
          { field: "project_type", as: "project_type", type: "string" },
        ],
      ),

      // 3. Checklist template selector runCode
      n("3", "runCode", "Select Kickoff Checklist by Project Type", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Select appropriate kickoff checklist based on project type
const projectType = (vars.project_type || "standard").toLowerCase();
const templates = {
  software: { tasks: ["Define technical architecture", "Set up development environment", "Configure CI/CD pipeline", "Create user story backlog", "Establish code review process", "Set up monitoring and alerting", "Define API contracts", "Security review kickoff"], category: "Engineering" },
  marketing: { tasks: ["Define target audience personas", "Set campaign KPIs and benchmarks", "Approve creative brief", "Configure analytics tracking", "Set up A/B testing framework", "Establish content calendar", "Vendor and agency onboarding", "Budget allocation by channel"], category: "Marketing" },
  consulting: { tasks: ["Client stakeholder mapping", "Statement of work finalization", "Resource allocation confirmation", "Communication cadence agreement", "Risk assessment workshop", "Deliverable milestone planning", "Quality assurance framework", "Change management plan"], category: "Consulting" },
  standard: { tasks: ["Stakeholder identification", "Scope confirmation meeting", "Resource assignment", "Communication plan setup", "Risk register initialization", "Milestone timeline creation", "Budget baseline approval", "Kickoff meeting preparation"], category: "General" }
};
const selected = templates[projectType] || templates.standard;
const teamList = (vars.team_members || "").split(",").map(m => m.trim()).filter(Boolean);
return {
  kickoff_tasks: selected.tasks.join(" | "),
  total_kickoff_tasks: selected.tasks.length,
  project_category: selected.category,
  team_size: teamList.length,
  channel_name: "proj-" + (vars.project_id || "new").toLowerCase().replace(/[^a-z0-9]/g, "-"),
  kickoff_date: vars.start_date || new Date().toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "kickoff_tasks", type: "string" },
            { name: "total_kickoff_tasks", type: "number" },
            { name: "project_category", type: "string" },
            { name: "team_size", type: "number" },
            { name: "channel_name", type: "string" },
            { name: "kickoff_date", type: "string" },
          ],
        },
      }),

      // 4. Task generation Asana API
      n("4", "hitApi", "Create Kickoff Tasks in Asana", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"Kickoff: {project_name} ({project_id})","notes":"Project Type: {project_category}\\nBudget: {budget}\\nTimeline: {start_date} to {end_date}\\n\\nKickoff Tasks:\\n{kickoff_tasks}","projects":["{asana_project_id}"],"due_on":"{kickoff_date}","assignee":"{pm_email}"}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "asana_task_id", type: "string" },
            {
              name: "data.permalink_url",
              customName: "asana_task_url",
              type: "string",
            },
          ],
        },
      }),

      // 5. Channel workspace setup Slack API
      n("5", "hitApi", "Create Project Slack Channel", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://slack.com/api/conversations.create",
          headers: [
            { key: "Authorization", value: "Bearer {slack_bot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "{channel_name}" },
            { key: "is_private", value: "false" },
          ],
          variables: [
            {
              name: "channel.id",
              customName: "slack_channel_id",
              type: "string",
            },
            {
              name: "channel.name",
              customName: "slack_channel_name",
              type: "string",
            },
          ],
        },
      }),

      // 6. Stakeholder notification email
      emailNode(
        "6",
        "Send Kickoff Notification to Stakeholders",
        1240,
        "{pm_email}",
        "Project Kickoff: {project_name} ({project_id}) — Starting {kickoff_date}",
        "<h2>Project Kickoff Notification</h2><p><strong>{project_name}</strong> ({project_id}) has been approved and is ready for kickoff!</p><ul><li><strong>Project Type:</strong> {project_category}</li><li><strong>Budget:</strong> {budget}</li><li><strong>Timeline:</strong> {start_date} to {end_date}</li><li><strong>Team Size:</strong> {team_size} members</li><li><strong>Sponsor:</strong> {sponsor_name}</li></ul><h3>Key Deliverables</h3><p>{key_deliverables}</p><h3>Kickoff Tasks ({total_kickoff_tasks})</h3><p>{kickoff_tasks}</p><p>Slack Channel: #{slack_channel_name} | Asana: {asana_task_url}</p>",
      ),

      // 7. Kickoff meeting scheduler Google Calendar API
      n("7", "hitApi", "Schedule Kickoff Meeting", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/calendars/primary/events",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "summary", value: "Project Kickoff: {project_name}" },
            {
              key: "description",
              value:
                "Kickoff meeting for {project_name} ({project_id}).\\nBudget: {budget}\\nDeliverables: {key_deliverables}\\nAsana: {asana_task_url}\\nSlack: #{slack_channel_name}",
            },
            {
              key: "start",
              value:
                '{"dateTime":"{kickoff_date}T09:00:00-05:00","timeZone":"America/Chicago"}',
            },
            {
              key: "end",
              value:
                '{"dateTime":"{kickoff_date}T10:30:00-05:00","timeZone":"America/Chicago"}',
            },
            {
              key: "attendees",
              value: '[{"email":"{pm_email}"},{"email":"{sponsor_email}"}]',
            },
          ],
          variables: [
            { name: "id", customName: "kickoff_event_id", type: "string" },
            {
              name: "htmlLink",
              customName: "kickoff_event_link",
              type: "string",
            },
          ],
        },
      }),

      // 8. Tool provisioning API
      n("8", "hitApi", "Provision Project Tools and Repos", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.github.com/orgs/{github_org}/repos",
          headers: [
            { key: "Authorization", value: "Bearer {github_token}" },
            { key: "Accept", value: "application/vnd.github+json" },
          ],
          body: [
            { key: "name", value: "{channel_name}" },
            {
              key: "description",
              value: "Repository for {project_name} ({project_id})",
            },
            { key: "private", value: "true" },
            { key: "auto_init", value: "true" },
          ],
          variables: [
            { name: "id", customName: "repo_id", type: "string" },
            { name: "html_url", customName: "repo_url", type: "string" },
          ],
        },
      }),

      // 9. Risk register initialization Sheets
      sheetsNode(
        "9",
        "Initialize Risk Register in Sheets",
        1960,
        "RiskRegister",
        '[["{project_id}","{project_name}","{project_category}","{budget}","{start_date}","{end_date}","{team_size}","{total_kickoff_tasks}","INITIATED","{kickoff_date}","{asana_task_url}","{repo_url}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1397. PROJECT RISK ASSESSMENT BOT (8 nodes)
  // ===================================================================

  "Project Risk Assessment Bot": {
    summary:
      "Triggers on risk assessment request webhooks, extracts project data with AI, aggregates project metrics from Jira API, performs deep risk analysis with AI, compares against historical risk data with runCode, generates mitigation suggestions with AI, updates the risk register in Google Sheets, and alerts stakeholders via Slack.",
    nodes: [
      trigger("Webhook: Risk assessment request", "webhook_app", {
        source: "project_management",
        event: "project.risk_assessment_requested",
        howItStarts:
          "Fires when a project manager or PMO requests a risk assessment for an active project, including the project key, assessment reason, and any known concerns",
        setup:
          "Configure webhook from your PMO request form. Set OpenAI API key for AI analysis. Configure Jira API for project data. Set Google Sheets for risk register. Set Slack webhook for stakeholder alerts.",
        inputFields: [],
      }),

      // 2. AI extract project data
      aiNode(
        "2",
        "AI Extract Risk Assessment Request",
        280,
        "You are a risk management assistant. Extract the project details and risk assessment parameters from the webhook payload including project key, project name, PM email, assessment reason, known risks, project phase, and priority level. Respond JSON: {project_key: string, project_name: string, pm_email: string, assessment_reason: string, known_risks: string, project_phase: string, priority_level: string, stakeholder_channel: string}",
        "Raw webhook payload: {webhook_payload}. Extract all risk assessment request details regardless of format.",
        [
          { field: "project_key", as: "project_key", type: "string" },
          { field: "project_name", as: "project_name", type: "string" },
          { field: "pm_email", as: "pm_email", type: "string" },
          {
            field: "assessment_reason",
            as: "assessment_reason",
            type: "string",
          },
          { field: "known_risks", as: "known_risks", type: "string" },
          { field: "project_phase", as: "project_phase", type: "string" },
          { field: "priority_level", as: "priority_level", type: "string" },
          {
            field: "stakeholder_channel",
            as: "stakeholder_channel",
            type: "string",
          },
        ],
      ),

      // 3. Project data aggregator Jira API
      n("3", "hitApi", "Fetch Project Metrics from Jira", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/search?jql=project={project_key}&maxResults=100&fields=status,priority,assignee,duedate,issuetype",
          headers: [
            { key: "Authorization", value: "Basic {jira_api_token}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "total", customName: "total_issues", type: "number" },
            { name: "issues", customName: "jira_issues", type: "string" },
          ],
        },
      }),

      // 4. AI risk analyzer
      aiNode(
        "4",
        "AI Deep Risk Analysis",
        760,
        "You are a senior risk analyst. Analyze the project data to identify risks across these categories: schedule risk, resource risk, technical risk, scope risk, budget risk, and external dependency risk. Score each 1-10 and provide an overall risk score. Respond JSON: {overall_risk_score: number, schedule_risk: number, resource_risk: number, technical_risk: number, scope_risk: number, budget_risk: number, external_risk: number, top_risks: string, risk_summary: string, risk_trend: string, confidence_level: string}",
        "Project: {project_name} ({project_key}). Phase: {project_phase}. Assessment reason: {assessment_reason}. Known risks: {known_risks}. Total Jira issues: {total_issues}. Issue data: {jira_issues}. Perform a comprehensive risk analysis.",
        [
          {
            field: "overall_risk_score",
            as: "overall_risk_score",
            type: "number",
          },
          { field: "schedule_risk", as: "schedule_risk", type: "number" },
          { field: "resource_risk", as: "resource_risk", type: "number" },
          { field: "technical_risk", as: "technical_risk", type: "number" },
          { field: "scope_risk", as: "scope_risk", type: "number" },
          { field: "budget_risk", as: "budget_risk", type: "number" },
          { field: "external_risk", as: "external_risk", type: "number" },
          { field: "top_risks", as: "top_risks", type: "string" },
          { field: "risk_summary", as: "risk_summary", type: "string" },
          { field: "risk_trend", as: "risk_trend", type: "string" },
          { field: "confidence_level", as: "confidence_level", type: "string" },
        ],
      ),

      // 5. Historical risk comparison runCode
      n("5", "runCode", "Compare Against Historical Risk Benchmarks", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compare current risk scores against historical benchmarks
const overall = parseInt(vars.overall_risk_score || "5", 10);
const schedule = parseInt(vars.schedule_risk || "5", 10);
const resource = parseInt(vars.resource_risk || "5", 10);
const technical = parseInt(vars.technical_risk || "5", 10);
const scope = parseInt(vars.scope_risk || "5", 10);
const budget = parseInt(vars.budget_risk || "5", 10);
// Historical averages by phase
const phaseAverages = { planning: 4, execution: 6, testing: 5, deployment: 7, closing: 3 };
const phase = (vars.project_phase || "execution").toLowerCase();
const benchmark = phaseAverages[phase] || 5;
const deviation = overall - benchmark;
const riskLevel = overall >= 8 ? "CRITICAL" : overall >= 6 ? "HIGH" : overall >= 4 ? "MEDIUM" : "LOW";
const highestRisk = Math.max(schedule, resource, technical, scope, budget);
const highestCategory = [["Schedule",schedule],["Resource",resource],["Technical",technical],["Scope",scope],["Budget",budget]].sort((a,b) => b[1] - a[1])[0][0];
const requiresEscalation = overall >= 7 ? "yes" : "no";
return {
  risk_level: riskLevel,
  benchmark_score: benchmark,
  deviation_from_benchmark: deviation,
  highest_risk_category: highestCategory,
  highest_risk_value: highestRisk,
  requires_escalation: requiresEscalation,
  assessment_date: new Date().toISOString().split("T")[0],
  risk_heat_map: "Schedule:" + schedule + " Resource:" + resource + " Technical:" + technical + " Scope:" + scope + " Budget:" + budget
};`,
          outputVariables: [
            { name: "risk_level", type: "string" },
            { name: "benchmark_score", type: "number" },
            { name: "deviation_from_benchmark", type: "number" },
            { name: "highest_risk_category", type: "string" },
            { name: "highest_risk_value", type: "number" },
            { name: "requires_escalation", type: "string" },
            { name: "assessment_date", type: "string" },
            { name: "risk_heat_map", type: "string" },
          ],
        },
      }),

      // 6. Risk mitigation suggester AI
      aiNode(
        "6",
        "AI Generate Risk Mitigation Recommendations",
        1240,
        "You are a risk mitigation strategist. Based on the identified risks and their scores, recommend specific, actionable mitigation strategies for the top risks. Prioritize by impact and feasibility. Include owners and timelines for each action. Respond JSON: {mitigation_plan: string, top_3_actions: string, resource_needs: string, estimated_cost: string, implementation_timeline: string}",
        "Project: {project_name}. Risk level: {risk_level}. Overall score: {overall_risk_score}/10. Highest risk: {highest_risk_category} ({highest_risk_value}/10). Heat map: {risk_heat_map}. Top risks: {top_risks}. Phase: {project_phase}. Known risks: {known_risks}. Generate actionable mitigation recommendations.",
        [
          { field: "mitigation_plan", as: "mitigation_plan", type: "string" },
          { field: "top_3_actions", as: "top_3_actions", type: "string" },
          { field: "resource_needs", as: "resource_needs", type: "string" },
          { field: "estimated_cost", as: "estimated_cost", type: "string" },
          {
            field: "implementation_timeline",
            as: "implementation_timeline",
            type: "string",
          },
        ],
      ),

      // 7. Risk register update Sheets
      sheetsNode(
        "7",
        "Update Risk Register in Sheets",
        1480,
        "RiskRegister",
        '[["{assessment_date}","{project_key}","{project_name}","{project_phase}","{overall_risk_score}","{risk_level}","{schedule_risk}","{resource_risk}","{technical_risk}","{scope_risk}","{budget_risk}","{highest_risk_category}","{top_risks}","{top_3_actions}","{requires_escalation}"]]',
      ),

      // 8. Stakeholder alert Slack
      slackNode(
        "8",
        "Alert Stakeholders of Risk Assessment",
        1720,
        "Risk Assessment Complete: {project_name} ({project_key})\\n*Overall Risk:* {overall_risk_score}/10 ({risk_level})\\n*Phase:* {project_phase} | *Benchmark:* {benchmark_score}/10 (Deviation: {deviation_from_benchmark})\\n*Heat Map:* {risk_heat_map}\\n*Top Risks:* {top_risks}\\n*Highest Category:* {highest_risk_category} ({highest_risk_value}/10)\\n*Trend:* {risk_trend}\\n*Top Mitigations:* {top_3_actions}\\n*Escalation Required:* {requires_escalation}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1398. PROJECT STATUS REPORT GENERATOR (9 nodes)
  // ===================================================================

  "Project Status Report Generator": {
    summary:
      "Runs on a weekly schedule, fetches sprint data from Jira, pulls GitHub activity metrics, retrieves budget and timeline data from Sheets, calculates team capacity with runCode, composes a comprehensive status report with AI, formats it with runCode, distributes to stakeholders via email, and archives to Google Sheets.",
    nodes: [
      trigger("Schedule: Weekly status report generation", "schedule", {
        schedule: "Every Monday at 8:00 AM",
        howItStarts:
          "Runs every Monday morning to automatically generate and distribute comprehensive project status reports combining data from Jira, GitHub, and budget tracking systems",
        setup:
          "Configure Jira API credentials for sprint data. Set GitHub API token for activity metrics. Provide Google Sheets spreadsheet ID for budget data. Set OpenAI API key for report composition. Configure email for distribution.",
        inputFields: [],
      }),

      // 2. Jira sprint data fetch API
      n("2", "hitApi", "Fetch Active Sprint Data from Jira", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{jira_domain}.atlassian.net/rest/agile/1.0/board/{board_id}/sprint?state=active",
          headers: [
            { key: "Authorization", value: "Basic {jira_api_token}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "values[0].name",
              customName: "sprint_name",
              type: "string",
            },
            {
              name: "values[0].goal",
              customName: "sprint_goal",
              type: "string",
            },
            {
              name: "values[0].startDate",
              customName: "sprint_start",
              type: "string",
            },
            {
              name: "values[0].endDate",
              customName: "sprint_end",
              type: "string",
            },
            { name: "values[0].id", customName: "sprint_id", type: "string" },
          ],
        },
      }),

      // 3. GitHub activity fetch API
      n("3", "hitApi", "Fetch GitHub Activity Metrics", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.github.com/repos/{github_org}/{github_repo}/stats/commit_activity",
          headers: [
            { key: "Authorization", value: "Bearer {github_token}" },
            { key: "Accept", value: "application/vnd.github+json" },
          ],
          body: [],
          variables: [
            { name: "[0].total", customName: "weekly_commits", type: "number" },
            {
              name: "[0].days",
              customName: "daily_commit_breakdown",
              type: "string",
            },
          ],
        },
      }),

      // 4. Budget timeline fetch Sheets
      n("4", "hitApi", "Fetch Budget and Timeline Data from Sheets", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/BudgetTracker!A2:F10",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "budget_data", type: "string" },
            { name: "range", customName: "budget_range", type: "string" },
          ],
        },
      }),

      // 5. Team capacity data runCode
      n("5", "runCode", "Calculate Team Capacity and Velocity", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate team capacity metrics and sprint health
const sprintName = vars.sprint_name || "Current Sprint";
const sprintGoal = vars.sprint_goal || "No goal set";
const weeklyCommits = parseInt(vars.weekly_commits || "0", 10);
const budgetRows = JSON.parse(vars.budget_data || "[]");
let totalBudget = 0;
let spentBudget = 0;
for (const row of budgetRows) {
  totalBudget += parseFloat(row[2] || "0");
  spentBudget += parseFloat(row[3] || "0");
}
const budgetRemaining = totalBudget - spentBudget;
const budgetUtilization = totalBudget > 0 ? Math.round((spentBudget / totalBudget) * 100) : 0;
const budgetHealth = budgetUtilization > 90 ? "AT_RISK" : budgetUtilization > 75 ? "WATCH" : "HEALTHY";
const velocityTrend = weeklyCommits > 30 ? "High" : weeklyCommits > 15 ? "Normal" : "Low";
const reportDate = new Date().toISOString().split("T")[0];
const reportWeek = "Week " + Math.ceil((new Date().getTime() - new Date(new Date().getFullYear(), 0, 1).getTime()) / (7 * 24 * 60 * 60 * 1000));
return {
  report_date: reportDate,
  report_week: reportWeek,
  total_budget: totalBudget,
  spent_budget: spentBudget,
  budget_remaining: budgetRemaining,
  budget_utilization: budgetUtilization,
  budget_health: budgetHealth,
  velocity_trend: velocityTrend,
  sprint_summary: sprintName + " — Goal: " + sprintGoal
};`,
          outputVariables: [
            { name: "report_date", type: "string" },
            { name: "report_week", type: "string" },
            { name: "total_budget", type: "number" },
            { name: "spent_budget", type: "number" },
            { name: "budget_remaining", type: "number" },
            { name: "budget_utilization", type: "number" },
            { name: "budget_health", type: "string" },
            { name: "velocity_trend", type: "string" },
            { name: "sprint_summary", type: "string" },
          ],
        },
      }),

      // 6. AI report composer
      aiNode(
        "6",
        "AI Compose Weekly Status Report",
        1240,
        "You are a project reporting specialist. Compose a professional weekly status report that includes an executive summary, sprint progress, development velocity, budget status, risks and blockers, and next week priorities. Use a clear, concise business tone. Respond JSON: {executive_summary: string, sprint_progress: string, development_highlights: string, risks_and_blockers: string, next_week_priorities: string, overall_health: string, action_items: string}",
        "Report week: {report_week} ({report_date}). Sprint: {sprint_summary}. Sprint dates: {sprint_start} to {sprint_end}. Weekly commits: {weekly_commits}. Velocity: {velocity_trend}. Budget: ${total_budget} total, ${spent_budget} spent, ${budget_remaining} remaining ({budget_utilization}% used, {budget_health}). Compose a comprehensive weekly status report.",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "sprint_progress", as: "sprint_progress", type: "string" },
          {
            field: "development_highlights",
            as: "development_highlights",
            type: "string",
          },
          {
            field: "risks_and_blockers",
            as: "risks_and_blockers",
            type: "string",
          },
          {
            field: "next_week_priorities",
            as: "next_week_priorities",
            type: "string",
          },
          { field: "overall_health", as: "overall_health", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
        ],
      ),

      // 7. Report formatter runCode
      n("7", "runCode", "Format Status Report as HTML", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Format the AI-composed report into structured HTML
const healthColors = { HEALTHY: "#27ae60", WATCH: "#f39c12", AT_RISK: "#e74c3c" };
const healthColor = healthColors[vars.budget_health] || "#333";
const reportHtml = "<h1>Weekly Status Report — " + (vars.report_week || "Current") + "</h1>" +
  "<p><strong>Date:</strong> " + (vars.report_date || "N/A") + " | <strong>Overall Health:</strong> <span style='color:" + healthColor + "'>" + (vars.overall_health || "N/A") + "</span></p>" +
  "<hr>" +
  "<h2>Executive Summary</h2><p>" + (vars.executive_summary || "") + "</p>" +
  "<h2>Sprint Progress</h2><p>" + (vars.sprint_progress || "") + "</p>" +
  "<p><em>" + (vars.sprint_summary || "") + "</em></p>" +
  "<h2>Development Highlights</h2><p>" + (vars.development_highlights || "") + "</p>" +
  "<p>Weekly Commits: " + (vars.weekly_commits || "0") + " | Velocity: " + (vars.velocity_trend || "N/A") + "</p>" +
  "<h2>Budget Status</h2>" +
  "<table border='1' cellpadding='5'><tr><td>Total Budget</td><td>$" + (vars.total_budget || "0") + "</td></tr>" +
  "<tr><td>Spent</td><td>$" + (vars.spent_budget || "0") + "</td></tr>" +
  "<tr><td>Remaining</td><td>$" + (vars.budget_remaining || "0") + "</td></tr>" +
  "<tr><td>Utilization</td><td>" + (vars.budget_utilization || "0") + "% (" + (vars.budget_health || "N/A") + ")</td></tr></table>" +
  "<h2>Risks & Blockers</h2><p>" + (vars.risks_and_blockers || "None identified") + "</p>" +
  "<h2>Next Week Priorities</h2><p>" + (vars.next_week_priorities || "") + "</p>" +
  "<h2>Action Items</h2><p>" + (vars.action_items || "None") + "</p>";
return { report_html: reportHtml, report_formatted: "yes" };`,
          outputVariables: [
            { name: "report_html", type: "string" },
            { name: "report_formatted", type: "string" },
          ],
        },
      }),

      // 8. Stakeholder distribution email
      emailNode(
        "8",
        "Distribute Weekly Status Report",
        1720,
        "{stakeholder_email}",
        "Weekly Status Report: {report_week} — {overall_health} | Budget: {budget_health}",
        "{report_html}",
      ),

      // 9. Report archive Sheets
      sheetsNode(
        "9",
        "Archive Status Report to Sheets",
        1960,
        "StatusReports",
        '[["{report_date}","{report_week}","{sprint_name}","{weekly_commits}","{velocity_trend}","{total_budget}","{spent_budget}","{budget_utilization}%","{budget_health}","{overall_health}","{executive_summary}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1399. PROJECT STATUS UPDATE BOT (7 nodes)
  // ===================================================================

  "Project Status Update Bot": {
    summary:
      "Receives status queries via webhook, extracts the query with AI, fetches project data from Jira API, interprets the status with AI, posts proactive updates to Slack, checks for blockers with a condition, and suggests actions for blocked items via Slack.",
    nodes: [
      trigger("Webhook: Project status query received", "webhook_app", {
        source: "slack_bot",
        event: "status.query_received",
        howItStarts:
          "Fires when a team member or stakeholder asks for a project status update through a Slack bot command, chat interface, or API call",
        setup:
          "Configure webhook from your Slack bot or chat interface. Set OpenAI API key for intelligent interpretation. Configure Jira API for project data. Set Slack webhook for response delivery.",
        inputFields: [],
      }),

      // 2. AI extract query
      aiNode(
        "2",
        "AI Parse Status Query Intent",
        280,
        "You are a project status bot. Parse the incoming query to understand what project, sprint, or team the user is asking about. Identify the query scope (specific task, sprint, overall project) and the detail level needed. Respond JSON: {project_key: string, query_scope: string, detail_level: string, specific_item: string, requester_id: string, response_channel: string}",
        "Raw webhook payload: {webhook_payload}. Parse the status query intent regardless of how it was phrased.",
        [
          { field: "project_key", as: "project_key", type: "string" },
          { field: "query_scope", as: "query_scope", type: "string" },
          { field: "detail_level", as: "detail_level", type: "string" },
          { field: "specific_item", as: "specific_item", type: "string" },
          { field: "requester_id", as: "requester_id", type: "string" },
          { field: "response_channel", as: "response_channel", type: "string" },
        ],
      ),

      // 3. Project data fetcher Jira API
      n("3", "hitApi", "Fetch Project Data from Jira", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/search?jql=project={project_key}+ORDER+BY+updated+DESC&maxResults=50&fields=summary,status,priority,assignee,duedate",
          headers: [
            { key: "Authorization", value: "Basic {jira_api_token}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "total", customName: "total_issues", type: "number" },
            { name: "issues", customName: "jira_issues", type: "string" },
          ],
        },
      }),

      // 4. AI status interpreter
      aiNode(
        "4",
        "AI Interpret and Summarize Project Status",
        760,
        "You are a project status interpreter. Analyze the Jira data and generate a clear, actionable status summary. Highlight progress, blockers, upcoming deadlines, and team workload. Tailor the response to the requested scope and detail level. Respond JSON: {status_summary: string, progress_percentage: number, open_blockers: string, blocker_count: number, upcoming_deadlines: string, team_workload: string, recommendation: string}",
        "Project: {project_key}. Scope: {query_scope}. Detail level: {detail_level}. Specific item: {specific_item}. Total issues: {total_issues}. Issue data: {jira_issues}. Generate a clear status summary.",
        [
          { field: "status_summary", as: "status_summary", type: "string" },
          {
            field: "progress_percentage",
            as: "progress_percentage",
            type: "number",
          },
          { field: "open_blockers", as: "open_blockers", type: "string" },
          { field: "blocker_count", as: "blocker_count", type: "number" },
          {
            field: "upcoming_deadlines",
            as: "upcoming_deadlines",
            type: "string",
          },
          { field: "team_workload", as: "team_workload", type: "string" },
          { field: "recommendation", as: "recommendation", type: "string" },
        ],
      ),

      // 5. Proactive status update Slack
      slackNode(
        "5",
        "Post Status Update to Slack",
        1000,
        "Project Status: {project_key}\\n*Progress:* {progress_percentage}%\\n*Summary:* {status_summary}\\n*Upcoming Deadlines:* {upcoming_deadlines}\\n*Team Workload:* {team_workload}\\n*Recommendation:* {recommendation}",
      ),

      // 6. Blocker detection condition
      n("6", "condition", "Any Open Blockers Detected?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "blocker_count", operator: "gt", value: "0" },
          ],
        },
      }),

      // 7. Action suggestion Slack (True path — blockers exist)
      slackNode(
        "7",
        "Post Blocker Resolution Suggestions",
        1480,
        "Blocker Alert for {project_key}\\n*{blocker_count} blockers detected:*\\n{open_blockers}\\n\\n*Suggested Actions:*\\n{recommendation}\\n\\nPlease address these blockers to maintain sprint velocity.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
    ],
  },

  // ===================================================================
  // 1400. QUARTERLY OKR CHECK-IN CAMPAIGN (8 nodes)
  // ===================================================================

  "Quarterly OKR Check-in Campaign": {
    summary:
      "Runs on a quarterly schedule, fetches OKR data from Google Sheets, distributes check-in requests via Slack, waits 5 days for responses, collects and aggregates responses with runCode, performs AI-powered progress analysis, sends manager digest emails, and updates the executive dashboard in Google Sheets.",
    nodes: [
      trigger("Schedule: Quarterly OKR check-in", "schedule", {
        schedule: "First Monday of January, April, July, October at 9:00 AM",
        howItStarts:
          "Runs at the start of each quarter to initiate OKR check-in campaigns, collecting progress updates from all teams on their objectives and key results",
        setup:
          "Configure Google Sheets with OKR tracking spreadsheet. Set Slack webhook for team notifications. Set OpenAI API key for progress analysis. Configure email for manager digests.",
        inputFields: [],
      }),

      // 2. OKR data fetch Sheets
      n("2", "hitApi", "Fetch Current OKR Data from Sheets", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/OKRTracker!A2:J200",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "okr_data", type: "string" },
            { name: "range", customName: "okr_range", type: "string" },
          ],
        },
      }),

      // 3. Check-in request distribution Slack
      slackNode(
        "3",
        "Distribute OKR Check-In Requests via Slack",
        520,
        "Quarterly OKR Check-In Time!\\n\\nIt's time to update your OKR progress for this quarter. Please update your key results in the OKR tracker spreadsheet within the next 5 business days.\\n\\nWhat to update:\\n1. Current progress percentage for each Key Result\\n2. Confidence level (on track / at risk / off track)\\n3. Blockers or dependencies\\n4. Updated target if scope changed\\n\\nSpreadsheet: Check your team channel for the direct link.\\n\\nDeadline: 5 business days from today.",
      ),

      // 4. Delay 5 days
      n("4", "delayAction", "Wait 5 Days for OKR Updates", 760, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 5. Response collection runCode
      n("5", "runCode", "Aggregate OKR Check-In Responses", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Aggregate OKR data and calculate progress metrics
const rows = JSON.parse(vars.okr_data || "[]");
let totalObjectives = 0;
let completedKRs = 0;
let totalKRs = 0;
let atRiskCount = 0;
let offTrackCount = 0;
let onTrackCount = 0;
const teamProgress = {};
for (const row of rows) {
  const team = row[1] || "Unknown";
  const progress = parseFloat(row[5] || "0");
  const confidence = (row[6] || "on track").toLowerCase();
  totalKRs++;
  if (progress >= 100) completedKRs++;
  if (confidence.includes("at risk")) atRiskCount++;
  else if (confidence.includes("off track")) offTrackCount++;
  else onTrackCount++;
  if (!teamProgress[team]) teamProgress[team] = { total: 0, sum: 0 };
  teamProgress[team].total++;
  teamProgress[team].sum += progress;
}
const overallProgress = totalKRs > 0 ? Math.round((rows.reduce((s, r) => s + parseFloat(r[5] || "0"), 0)) / totalKRs) : 0;
const teamSummaries = Object.entries(teamProgress).map(([team, data]) => team + ": " + Math.round(data.sum / data.total) + "% avg").join(" | ");
const quarterHealth = offTrackCount > totalKRs * 0.3 ? "CRITICAL" : atRiskCount > totalKRs * 0.3 ? "AT_RISK" : "HEALTHY";
return {
  total_key_results: totalKRs,
  completed_key_results: completedKRs,
  overall_progress: overallProgress,
  on_track_count: onTrackCount,
  at_risk_count: atRiskCount,
  off_track_count: offTrackCount,
  team_summaries: teamSummaries,
  quarter_health: quarterHealth,
  check_in_date: new Date().toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "total_key_results", type: "number" },
            { name: "completed_key_results", type: "number" },
            { name: "overall_progress", type: "number" },
            { name: "on_track_count", type: "number" },
            { name: "at_risk_count", type: "number" },
            { name: "off_track_count", type: "number" },
            { name: "team_summaries", type: "string" },
            { name: "quarter_health", type: "string" },
            { name: "check_in_date", type: "string" },
          ],
        },
      }),

      // 6. AI progress analysis
      aiNode(
        "6",
        "AI Analyze OKR Progress and Trends",
        1240,
        "You are an OKR strategy advisor. Analyze the quarterly OKR progress data and provide insights on performance trends, team alignment, potential risks, and recommended actions. Identify which objectives need executive attention and where resources should be reallocated. Respond JSON: {progress_analysis: string, top_performing_areas: string, areas_needing_attention: string, resource_recommendations: string, executive_highlights: string, next_quarter_suggestions: string}",
        "Quarter check-in ({check_in_date}). Total KRs: {total_key_results}. Completed: {completed_key_results}. Overall progress: {overall_progress}%. On track: {on_track_count}. At risk: {at_risk_count}. Off track: {off_track_count}. Health: {quarter_health}. Team breakdown: {team_summaries}. Provide strategic analysis.",
        [
          {
            field: "progress_analysis",
            as: "progress_analysis",
            type: "string",
          },
          {
            field: "top_performing_areas",
            as: "top_performing_areas",
            type: "string",
          },
          {
            field: "areas_needing_attention",
            as: "areas_needing_attention",
            type: "string",
          },
          {
            field: "resource_recommendations",
            as: "resource_recommendations",
            type: "string",
          },
          {
            field: "executive_highlights",
            as: "executive_highlights",
            type: "string",
          },
          {
            field: "next_quarter_suggestions",
            as: "next_quarter_suggestions",
            type: "string",
          },
        ],
      ),

      // 7. Manager digest email
      emailNode(
        "7",
        "Send OKR Digest to Managers",
        1480,
        "{manager_email}",
        "Quarterly OKR Check-In Report — {overall_progress}% Overall Progress ({quarter_health})",
        "<h2>Quarterly OKR Progress Report</h2><p><strong>Check-in Date:</strong> {check_in_date} | <strong>Health:</strong> {quarter_health}</p><h3>Overview</h3><ul><li>Total Key Results: {total_key_results}</li><li>Completed: {completed_key_results}</li><li>Overall Progress: {overall_progress}%</li><li>On Track: {on_track_count} | At Risk: {at_risk_count} | Off Track: {off_track_count}</li></ul><h3>Team Progress</h3><p>{team_summaries}</p><h3>Analysis</h3><p>{progress_analysis}</p><h3>Top Performing</h3><p>{top_performing_areas}</p><h3>Needs Attention</h3><p>{areas_needing_attention}</p><h3>Resource Recommendations</h3><p>{resource_recommendations}</p><h3>Executive Highlights</h3><p>{executive_highlights}</p>",
      ),

      // 8. Executive dashboard Sheets
      sheetsNode(
        "8",
        "Update Executive OKR Dashboard",
        1720,
        "OKRDashboard",
        '[["{check_in_date}","{total_key_results}","{completed_key_results}","{overall_progress}%","{on_track_count}","{at_risk_count}","{off_track_count}","{quarter_health}","{executive_highlights}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1401. QUOTE REQUEST FORM BOT (8 nodes)
  // ===================================================================

  "Quote Request Form Bot": {
    summary:
      "Receives quote requests via webhook, clarifies requirements with AI, calculates pricing with runCode, generates a custom quote document with AI, creates a CRM lead in HubSpot, delivers the quote via email, assigns the sales rep via Slack, and schedules a follow-up with a delay node.",
    nodes: [
      trigger("Webhook: Quote request form submitted", "webhook_app", {
        source: "website_form",
        event: "form.quote_request_submitted",
        howItStarts:
          "Fires when a prospect submits a quote request form on the website, including their contact info, requirements, budget range, and timeline needs",
        setup:
          "Configure webhook from your website quote request form. Set OpenAI API key for intelligent processing. Configure HubSpot API for CRM lead creation. Set email and Slack for delivery and notifications.",
        inputFields: [],
      }),

      // 2. AI requirements clarifier
      aiNode(
        "2",
        "AI Clarify and Structure Requirements",
        280,
        "You are a sales engineering assistant. Parse the quote request form submission to extract and clarify the prospect's requirements. Identify the product/service needed, quantity, specifications, timeline, budget range, company size, and decision urgency. Respond JSON: {contact_email: string, contact_name: string, company_name: string, company_size: string, product_service: string, quantity: string, specifications: string, timeline: string, budget_range: string, urgency: string, industry: string}",
        "Raw webhook payload: {webhook_payload}. Parse and structure the quote request regardless of form field naming.",
        [
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "company_size", as: "company_size", type: "string" },
          { field: "product_service", as: "product_service", type: "string" },
          { field: "quantity", as: "quantity", type: "string" },
          { field: "specifications", as: "specifications", type: "string" },
          { field: "timeline", as: "timeline", type: "string" },
          { field: "budget_range", as: "budget_range", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "industry", as: "industry", type: "string" },
        ],
      ),

      // 3. Pricing engine query runCode
      n("3", "runCode", "Calculate Custom Quote Pricing", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate custom pricing based on requirements
const product = (vars.product_service || "standard").toLowerCase();
const qty = parseInt(vars.quantity || "1", 10);
const urgency = (vars.urgency || "normal").toLowerCase();
const basePrices = { starter: 99, professional: 299, enterprise: 799, custom: 1499, standard: 199 };
const basePrice = basePrices[product] || 199;
const quantityDiscount = qty >= 100 ? 0.20 : qty >= 50 ? 0.15 : qty >= 20 ? 0.10 : qty >= 10 ? 0.05 : 0;
const urgencyMultiplier = urgency === "urgent" ? 1.25 : urgency === "rush" ? 1.5 : 1.0;
const unitPrice = Math.round(basePrice * (1 - quantityDiscount) * urgencyMultiplier);
const totalPrice = unitPrice * qty;
const setupFee = totalPrice > 5000 ? 0 : 500;
const quoteTotal = totalPrice + setupFee;
return {
  unit_price: unitPrice,
  total_price: totalPrice,
  setup_fee: setupFee,
  quote_total: quoteTotal,
  quantity_discount: Math.round(quantityDiscount * 100) + "%",
  urgency_surcharge: urgencyMultiplier > 1 ? Math.round((urgencyMultiplier - 1) * 100) + "%" : "None",
  quote_number: "QT-" + Date.now().toString(36).toUpperCase(),
  valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
  pricing_tier: product
};`,
          outputVariables: [
            { name: "unit_price", type: "number" },
            { name: "total_price", type: "number" },
            { name: "setup_fee", type: "number" },
            { name: "quote_total", type: "number" },
            { name: "quantity_discount", type: "string" },
            { name: "urgency_surcharge", type: "string" },
            { name: "quote_number", type: "string" },
            { name: "valid_until", type: "string" },
            { name: "pricing_tier", type: "string" },
          ],
        },
      }),

      // 4. AI custom quote generator
      aiNode(
        "4",
        "AI Generate Professional Quote Document",
        760,
        "You are a professional quote writer. Create a polished, detailed quote document with executive summary, scope of work, pricing breakdown, terms and conditions, and next steps. Make it persuasive yet professional. Respond JSON: {quote_summary: string, scope_of_work: string, pricing_breakdown: string, terms_conditions: string, next_steps: string, value_proposition: string}",
        "Prospect: {contact_name} at {company_name} ({industry}, {company_size}). Product: {product_service}. Quantity: {quantity}. Specs: {specifications}. Timeline: {timeline}. Quote #{quote_number}: ${unit_price}/unit x {quantity} = ${total_price} + ${setup_fee} setup = ${quote_total} total. Discount: {quantity_discount}. Valid until: {valid_until}. Create a professional quote document.",
        [
          { field: "quote_summary", as: "quote_summary", type: "string" },
          { field: "scope_of_work", as: "scope_of_work", type: "string" },
          {
            field: "pricing_breakdown",
            as: "pricing_breakdown",
            type: "string",
          },
          { field: "terms_conditions", as: "terms_conditions", type: "string" },
          { field: "next_steps", as: "next_steps", type: "string" },
          {
            field: "value_proposition",
            as: "value_proposition",
            type: "string",
          },
        ],
      ),

      // 5. CRM lead creation HubSpot
      hubspotPostNode(
        "5",
        "Create Lead in HubSpot CRM",
        1000,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{contact_email}","firstname":"{contact_name}","company":"{company_name}","industry":"{industry}","hs_lead_status":"NEW","notes_last_activity":"Quote {quote_number}: {product_service} x{quantity} = ${quote_total}. Timeline: {timeline}. Urgency: {urgency}"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          { name: "properties.email", customName: "hs_email", type: "string" },
        ],
      ),

      // 6. Quote delivery email
      emailNode(
        "6",
        "Deliver Quote to Prospect",
        1240,
        "{contact_email}",
        "Your Custom Quote — {quote_number} | {product_service} for {company_name}",
        "<h2>Quote {quote_number}</h2><p>Dear {contact_name},</p><p>{quote_summary}</p><hr><h3>Scope of Work</h3><p>{scope_of_work}</p><h3>Pricing</h3>{pricing_breakdown}<table border='1' cellpadding='5'><tr><td>Unit Price</td><td>${unit_price}</td></tr><tr><td>Quantity</td><td>{quantity}</td></tr><tr><td>Subtotal</td><td>${total_price}</td></tr><tr><td>Setup Fee</td><td>${setup_fee}</td></tr><tr><td><strong>Total</strong></td><td><strong>${quote_total}</strong></td></tr></table><p>Volume Discount: {quantity_discount} | Urgency: {urgency_surcharge}</p><h3>Terms</h3><p>{terms_conditions}</p><p><strong>Quote valid until: {valid_until}</strong></p><h3>Next Steps</h3><p>{next_steps}</p>",
      ),

      // 7. Sales rep assignment Slack
      slackNode(
        "7",
        "Assign Sales Rep via Slack",
        1480,
        "New Quote Generated — {quote_number}\\n*Prospect:* {contact_name} ({company_name}, {industry})\\n*Product:* {product_service} x{quantity}\\n*Quote Total:* ${quote_total}\\n*Timeline:* {timeline} | *Urgency:* {urgency}\\n*Budget Range:* {budget_range}\\n*HubSpot:* Contact #{hubspot_contact_id}\\n*Valid Until:* {valid_until}\\n*Value Prop:* {value_proposition}",
      ),

      // 8. Follow-up scheduler delay
      n("8", "delayAction", "Schedule 3-Day Follow-Up", 1720, {
        delayDetails: { duration: 3, unit: "days" },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1402. RACI MATRIX GENERATOR (7 nodes)
  // ===================================================================

  "RACI Matrix Generator": {
    summary:
      "Receives project input via webhook, extracts project details with AI, generates a comprehensive RACI matrix with AI, validates assignments with runCode, routes through an approval gate, publishes to Google Docs API, and notifies the team via Slack.",
    nodes: [
      trigger("Webhook: RACI generation request", "webhook_app", {
        source: "project_management",
        event: "project.raci_requested",
        howItStarts:
          "Fires when a project manager requests a RACI matrix for a new or existing project, including the project scope, team members, and key deliverables",
        setup:
          "Configure webhook from your PM tool or request form. Set OpenAI API key for AI generation. Configure Google Docs API for document publishing. Set Slack webhook for team notifications.",
        inputFields: [],
      }),

      // 2. AI extract project data
      aiNode(
        "2",
        "AI Extract Project Scope and Roles",
        280,
        "You are a project governance specialist. Extract the project details needed for RACI matrix creation from the webhook payload, including project name, deliverables, team members with their roles, key activities, and stakeholders. Respond JSON: {project_name: string, deliverables: string, team_members: string, team_roles: string, key_activities: string, stakeholders: string, pm_email: string, project_complexity: string}",
        "Raw webhook payload: {webhook_payload}. Extract all project information needed for RACI matrix generation regardless of format.",
        [
          { field: "project_name", as: "project_name", type: "string" },
          { field: "deliverables", as: "deliverables", type: "string" },
          { field: "team_members", as: "team_members", type: "string" },
          { field: "team_roles", as: "team_roles", type: "string" },
          { field: "key_activities", as: "key_activities", type: "string" },
          { field: "stakeholders", as: "stakeholders", type: "string" },
          { field: "pm_email", as: "pm_email", type: "string" },
          {
            field: "project_complexity",
            as: "project_complexity",
            type: "string",
          },
        ],
      ),

      // 3. AI RACI builder
      aiNode(
        "3",
        "AI Build Complete RACI Matrix",
        520,
        "You are a RACI matrix expert. Create a detailed RACI (Responsible, Accountable, Consulted, Informed) matrix for the project. Each activity must have exactly ONE Accountable person. Ensure no gaps — every activity-person intersection should have an assignment. Respond JSON: {raci_matrix: string, total_activities: number, total_assignments: number, accountability_gaps: string, matrix_summary: string, escalation_paths: string, decision_authority: string}",
        "Project: {project_name}. Deliverables: {deliverables}. Team: {team_members}. Roles: {team_roles}. Activities: {key_activities}. Stakeholders: {stakeholders}. Complexity: {project_complexity}. Generate a complete, validated RACI matrix.",
        [
          { field: "raci_matrix", as: "raci_matrix", type: "string" },
          { field: "total_activities", as: "total_activities", type: "number" },
          {
            field: "total_assignments",
            as: "total_assignments",
            type: "number",
          },
          {
            field: "accountability_gaps",
            as: "accountability_gaps",
            type: "string",
          },
          { field: "matrix_summary", as: "matrix_summary", type: "string" },
          { field: "escalation_paths", as: "escalation_paths", type: "string" },
          {
            field: "decision_authority",
            as: "decision_authority",
            type: "string",
          },
        ],
      ),

      // 4. Validation engine runCode
      n("4", "runCode", "Validate RACI Matrix Integrity", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate RACI matrix rules
const matrix = vars.raci_matrix || "";
const totalActivities = parseInt(vars.total_activities || "0", 10);
const gaps = vars.accountability_gaps || "none";
const hasGaps = gaps !== "none" && gaps.length > 0;
// Validation rules
const validations = [];
if (totalActivities === 0) validations.push("FAIL: No activities defined");
if (hasGaps) validations.push("WARNING: Accountability gaps found — " + gaps);
if (!matrix.includes("A")) validations.push("FAIL: No Accountable assignments found");
if (!matrix.includes("R")) validations.push("FAIL: No Responsible assignments found");
const passesValidation = !validations.some(v => v.startsWith("FAIL")) ? "yes" : "no";
const validationReport = validations.length > 0 ? validations.join(" | ") : "All validations passed";
return {
  passes_validation: passesValidation,
  validation_report: validationReport,
  validation_count: validations.length,
  matrix_quality: passesValidation === "yes" ? (hasGaps ? "GOOD_WITH_WARNINGS" : "EXCELLENT") : "NEEDS_REVISION",
  generated_date: new Date().toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "passes_validation", type: "string" },
            { name: "validation_report", type: "string" },
            { name: "validation_count", type: "number" },
            { name: "matrix_quality", type: "string" },
            { name: "generated_date", type: "string" },
          ],
        },
      }),

      // 5. Review gate approval
      n("5", "approvalGate", "RACI Matrix Review Gate", 1000, {
        approvalGateDetails: {
          approverEmail: "{pm_email}",
          approvalMessage:
            "RACI Matrix Ready for Review\\n\\nProject: {project_name}\\nActivities: {total_activities}\\nAssignments: {total_assignments}\\nQuality: {matrix_quality}\\n\\nValidation: {validation_report}\\n\\nMatrix Summary: {matrix_summary}\\nEscalation Paths: {escalation_paths}\\nDecision Authority: {decision_authority}\\n\\nApprove to publish the RACI matrix to the project documentation.",
        },
      }),

      // 6. Document publisher Google Docs API
      n("6", "hitApi", "Publish RACI Matrix to Google Docs", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://docs.googleapis.com/v1/documents",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "title",
              value: "RACI Matrix — {project_name} ({generated_date})",
            },
          ],
          variables: [
            { name: "documentId", customName: "doc_id", type: "string" },
            { name: "revisionId", customName: "doc_revision", type: "string" },
          ],
        },
      }),

      // 7. Team notification Slack
      slackNode(
        "7",
        "Notify Team of Published RACI Matrix",
        1480,
        "RACI Matrix Published: {project_name}\\n*Activities:* {total_activities} | *Assignments:* {total_assignments}\\n*Quality:* {matrix_quality}\\n*Summary:* {matrix_summary}\\n*Decision Authority:* {decision_authority}\\n*Escalation Paths:* {escalation_paths}\\n*Document:* https://docs.google.com/document/d/{doc_id}/edit\\n\\nPlease review your assigned responsibilities and confirm understanding.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1403. RFP RESPONSE GENERATOR (9 nodes)
  // ===================================================================

  "RFP Response Generator": {
    summary:
      "Receives RFP documents via webhook, extracts requirements with AI, parses sections with runCode, searches the knowledge base API for relevant content, drafts responses with AI, checks compliance with runCode, routes through an SME approval gate, formats the final document with runCode, and coordinates submission via email.",
    nodes: [
      trigger("Webhook: RFP intake received", "webhook_app", {
        source: "procurement_system",
        event: "rfp.intake_received",
        howItStarts:
          "Fires when a new RFP is received from a prospect or procurement department, including the RFP document content, requirements, deadline, and evaluation criteria",
        setup:
          "Configure webhook from your RFP intake system or email parser. Set OpenAI API key for AI analysis and response drafting. Configure knowledge base API for content retrieval. Set up approval routing for SME review.",
        inputFields: [],
      }),

      // 2. AI extract RFP data
      aiNode(
        "2",
        "AI Extract RFP Requirements",
        280,
        "You are an RFP analysis specialist. Parse the incoming RFP to extract the issuing organization, project scope, key requirements, evaluation criteria, submission deadline, format requirements, and mandatory sections. Respond JSON: {issuing_org: string, rfp_title: string, project_scope: string, key_requirements: string, evaluation_criteria: string, submission_deadline: string, format_requirements: string, mandatory_sections: string, contact_email: string, rfp_id: string, budget_indication: string}",
        "Raw webhook payload: {webhook_payload}. Extract all RFP details and requirements regardless of document format.",
        [
          { field: "issuing_org", as: "issuing_org", type: "string" },
          { field: "rfp_title", as: "rfp_title", type: "string" },
          { field: "project_scope", as: "project_scope", type: "string" },
          { field: "key_requirements", as: "key_requirements", type: "string" },
          {
            field: "evaluation_criteria",
            as: "evaluation_criteria",
            type: "string",
          },
          {
            field: "submission_deadline",
            as: "submission_deadline",
            type: "string",
          },
          {
            field: "format_requirements",
            as: "format_requirements",
            type: "string",
          },
          {
            field: "mandatory_sections",
            as: "mandatory_sections",
            type: "string",
          },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "rfp_id", as: "rfp_id", type: "string" },
          {
            field: "budget_indication",
            as: "budget_indication",
            type: "string",
          },
        ],
      ),

      // 3. RFP parser runCode
      n("3", "runCode", "Parse RFP Sections and Score Complexity", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse mandatory sections and score response complexity
const sections = (vars.mandatory_sections || "").split(",").map(s => s.trim()).filter(Boolean);
const requirements = (vars.key_requirements || "").split(",").map(r => r.trim()).filter(Boolean);
const criteria = (vars.evaluation_criteria || "").split(",").map(c => c.trim()).filter(Boolean);
const totalSections = sections.length;
const totalRequirements = requirements.length;
const complexity = totalSections > 10 ? "HIGH" : totalSections > 5 ? "MEDIUM" : "LOW";
const estimatedHours = totalSections * 2 + totalRequirements * 0.5;
const deadline = new Date(vars.submission_deadline || Date.now());
const daysRemaining = Math.max(0, Math.floor((deadline - new Date()) / (1000 * 60 * 60 * 24)));
const feasible = daysRemaining >= Math.ceil(estimatedHours / 8) ? "yes" : "tight_timeline";
return {
  total_sections: totalSections,
  total_requirements: totalRequirements,
  total_criteria: criteria.length,
  response_complexity: complexity,
  estimated_hours: Math.round(estimatedHours),
  days_remaining: daysRemaining,
  timeline_feasibility: feasible,
  section_list: sections.join(" | ") || "General response",
  response_id: "RSP-" + (vars.rfp_id || Date.now().toString(36).toUpperCase())
};`,
          outputVariables: [
            { name: "total_sections", type: "number" },
            { name: "total_requirements", type: "number" },
            { name: "total_criteria", type: "number" },
            { name: "response_complexity", type: "string" },
            { name: "estimated_hours", type: "number" },
            { name: "days_remaining", type: "number" },
            { name: "timeline_feasibility", type: "string" },
            { name: "section_list", type: "string" },
            { name: "response_id", type: "string" },
          ],
        },
      }),

      // 4. Knowledge base search API
      n("4", "hitApi", "Search Knowledge Base for RFP Content", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/embeddings",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "text-embedding-3-small" },
            { key: "input", value: "{key_requirements} {project_scope}" },
          ],
          variables: [
            {
              name: "data[0].embedding",
              customName: "search_embedding",
              type: "string",
            },
            {
              name: "usage.total_tokens",
              customName: "embed_tokens",
              type: "number",
            },
          ],
        },
      }),

      // 5. AI response drafter
      aiNode(
        "5",
        "AI Draft Comprehensive RFP Response",
        1000,
        "You are an expert RFP response writer. Draft a compelling, detailed response for each mandatory section. Highlight company strengths, relevant experience, and unique differentiators. Address every evaluation criterion directly. Use professional language and quantify claims where possible. Respond JSON: {executive_summary: string, technical_approach: string, team_qualifications: string, project_timeline: string, pricing_approach: string, references: string, response_body: string, competitive_advantages: string, win_themes: string}",
        "RFP: {rfp_title} from {issuing_org}. Scope: {project_scope}. Requirements: {key_requirements}. Criteria: {evaluation_criteria}. Sections: {section_list}. Budget: {budget_indication}. Response ID: {response_id}. Draft a winning RFP response covering all {total_sections} sections.",
        [
          { field: "executive_summary", as: "exec_summary", type: "string" },
          {
            field: "technical_approach",
            as: "technical_approach",
            type: "string",
          },
          {
            field: "team_qualifications",
            as: "team_qualifications",
            type: "string",
          },
          { field: "project_timeline", as: "project_timeline", type: "string" },
          { field: "pricing_approach", as: "pricing_approach", type: "string" },
          { field: "references", as: "references", type: "string" },
          { field: "response_body", as: "response_body", type: "string" },
          {
            field: "competitive_advantages",
            as: "competitive_advantages",
            type: "string",
          },
          { field: "win_themes", as: "win_themes", type: "string" },
        ],
      ),

      // 6. Compliance checker runCode
      n("6", "runCode", "Validate Response Compliance", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check that the response addresses all mandatory sections and requirements
const sections = (vars.section_list || "").split("|").map(s => s.trim().toLowerCase()).filter(Boolean);
const responseBody = (vars.response_body || "").toLowerCase();
const execSummary = (vars.exec_summary || "").toLowerCase();
const fullContent = responseBody + " " + execSummary + " " + (vars.technical_approach || "").toLowerCase();
const coveredSections = sections.filter(s => fullContent.includes(s.substring(0, 10)));
const missingSections = sections.filter(s => !fullContent.includes(s.substring(0, 10)));
const coverageRate = sections.length > 0 ? Math.round((coveredSections.length / sections.length) * 100) : 100;
const complianceStatus = coverageRate >= 90 ? "COMPLIANT" : coverageRate >= 70 ? "MOSTLY_COMPLIANT" : "NON_COMPLIANT";
return {
  coverage_rate: coverageRate,
  compliance_status: complianceStatus,
  covered_sections: coveredSections.length,
  missing_sections: missingSections.join(", ") || "none",
  compliance_notes: complianceStatus === "COMPLIANT" ? "Response addresses all mandatory sections" : "Review needed — missing sections: " + missingSections.join(", "),
  ready_for_review: coverageRate >= 70 ? "yes" : "no"
};`,
          outputVariables: [
            { name: "coverage_rate", type: "number" },
            { name: "compliance_status", type: "string" },
            { name: "covered_sections", type: "number" },
            { name: "missing_sections", type: "string" },
            { name: "compliance_notes", type: "string" },
            { name: "ready_for_review", type: "string" },
          ],
        },
      }),

      // 7. SME review approval gate
      n("7", "approvalGate", "SME Review and Approval Gate", 1480, {
        approvalGateDetails: {
          approverEmail: "{rfp_manager_email}",
          approvalMessage:
            "RFP Response Ready for SME Review\\n\\nRFP: {rfp_title} ({rfp_id}) from {issuing_org}\\nResponse ID: {response_id}\\nDeadline: {submission_deadline} ({days_remaining} days remaining)\\nComplexity: {response_complexity}\\n\\nCompliance: {compliance_status} ({coverage_rate}% coverage)\\nMissing Sections: {missing_sections}\\n\\nWin Themes: {win_themes}\\nCompetitive Advantages: {competitive_advantages}\\n\\nApprove to format and submit, or reject for revision.",
        },
      }),

      // 8. Document formatter runCode
      n("8", "runCode", "Format Final RFP Response Document", 1720, {
        runCodeDetails: {
          language: "javascript",
          code: `// Format the complete RFP response as a structured document
const formattedDoc = "<h1>Response to " + (vars.rfp_title || "RFP") + "</h1>" +
  "<p><strong>Response ID:</strong> " + (vars.response_id || "N/A") + " | <strong>Issuing Organization:</strong> " + (vars.issuing_org || "N/A") + "</p>" +
  "<hr>" +
  "<h2>Executive Summary</h2>" + (vars.exec_summary || "") +
  "<h2>Technical Approach</h2>" + (vars.technical_approach || "") +
  "<h2>Team Qualifications</h2>" + (vars.team_qualifications || "") +
  "<h2>Project Timeline</h2>" + (vars.project_timeline || "") +
  "<h2>Pricing Approach</h2>" + (vars.pricing_approach || "") +
  "<h2>References</h2>" + (vars.references || "") +
  "<hr>" +
  "<p><em>Compliance: " + (vars.compliance_status || "N/A") + " (" + (vars.coverage_rate || "0") + "% section coverage)</em></p>";
return {
  formatted_document: formattedDoc,
  document_ready: "yes",
  submission_date: new Date().toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "formatted_document", type: "string" },
            { name: "document_ready", type: "string" },
            { name: "submission_date", type: "string" },
          ],
        },
      }),

      // 9. Submission coordinator email
      emailNode(
        "9",
        "Submit RFP Response via Email",
        1960,
        "{contact_email}",
        "RFP Response: {rfp_title} ({response_id}) — {issuing_org}",
        "{formatted_document}<hr><p><strong>Submitted:</strong> {submission_date} | <strong>Deadline:</strong> {submission_deadline}</p><p>Please confirm receipt of this response. For questions, contact our team directly.</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1404. ROI CALCULATOR BOT (7 nodes)
  // ===================================================================

  "ROI Calculator Bot": {
    summary:
      "Receives ROI calculation requests via webhook, gathers input through an AI conversational intake, validates data completeness with AI, runs a comprehensive ROI calculation engine in runCode, generates multiple scenarios with runCode, delivers the report via email, and captures the lead in HubSpot CRM.",
    nodes: [
      trigger("Webhook: ROI calculation request", "webhook_app", {
        source: "website_tool",
        event: "tool.roi_calculation_requested",
        howItStarts:
          "Fires when a prospect uses the ROI calculator tool on the website, submitting their current metrics, costs, and goals for a return-on-investment analysis",
        setup:
          "Configure webhook from your website ROI calculator form. Set OpenAI API key for conversational AI. Configure email for report delivery. Set HubSpot API for CRM lead capture.",
        inputFields: [],
      }),

      // 2. AI conversational intake
      aiNode(
        "2",
        "AI Extract ROI Calculation Inputs",
        280,
        "You are an ROI analysis assistant. Extract the prospect's current operational metrics from the webhook payload: their email, name, company, industry, current annual cost, current process time, team size, error rate, and desired improvement goals. Respond JSON: {contact_email: string, contact_name: string, company_name: string, industry: string, current_annual_cost: string, current_process_hours: string, team_size: string, current_error_rate: string, improvement_goal: string, tool_category: string}",
        "Raw webhook payload: {webhook_payload}. Extract all ROI calculation inputs regardless of form field naming.",
        [
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "industry", as: "industry", type: "string" },
          {
            field: "current_annual_cost",
            as: "current_annual_cost",
            type: "string",
          },
          {
            field: "current_process_hours",
            as: "current_process_hours",
            type: "string",
          },
          { field: "team_size", as: "team_size", type: "string" },
          {
            field: "current_error_rate",
            as: "current_error_rate",
            type: "string",
          },
          { field: "improvement_goal", as: "improvement_goal", type: "string" },
          { field: "tool_category", as: "tool_category", type: "string" },
        ],
      ),

      // 3. AI data validator
      aiNode(
        "3",
        "AI Validate and Normalize Input Data",
        520,
        "You are a financial data validation assistant. Check the ROI input data for completeness and reasonableness. Normalize values to consistent formats. Flag any unrealistic numbers. Fill in industry benchmarks for missing data. Respond JSON: {validated_annual_cost: number, validated_hours: number, validated_team_size: number, validated_error_rate: number, data_quality: string, benchmark_notes: string, assumptions: string}",
        "Inputs — Annual cost: {current_annual_cost}. Process hours/week: {current_process_hours}. Team size: {team_size}. Error rate: {current_error_rate}. Industry: {industry}. Goal: {improvement_goal}. Validate and normalize all inputs for ROI calculation.",
        [
          {
            field: "validated_annual_cost",
            as: "validated_annual_cost",
            type: "number",
          },
          { field: "validated_hours", as: "validated_hours", type: "number" },
          {
            field: "validated_team_size",
            as: "validated_team_size",
            type: "number",
          },
          {
            field: "validated_error_rate",
            as: "validated_error_rate",
            type: "number",
          },
          { field: "data_quality", as: "data_quality", type: "string" },
          { field: "benchmark_notes", as: "benchmark_notes", type: "string" },
          { field: "assumptions", as: "assumptions", type: "string" },
        ],
      ),

      // 4. ROI calculation engine runCode
      n("4", "runCode", "Calculate Comprehensive ROI Metrics", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Comprehensive ROI calculation engine
const annualCost = parseFloat(vars.validated_annual_cost || "100000");
const weeklyHours = parseFloat(vars.validated_hours || "40");
const teamSize = parseInt(vars.validated_team_size || "5", 10);
const errorRate = parseFloat(vars.validated_error_rate || "5") / 100;
const avgHourlyRate = 75;
// Cost savings from automation
const hoursPerYear = weeklyHours * 52;
const automationEfficiency = 0.60; // 60% time savings
const hoursSaved = Math.round(hoursPerYear * automationEfficiency);
const laborSavings = Math.round(hoursSaved * avgHourlyRate);
// Error reduction savings
const errorCostPerYear = annualCost * errorRate * 2;
const errorReduction = 0.80;
const errorSavings = Math.round(errorCostPerYear * errorReduction);
// Total savings and ROI
const implementationCost = 50000;
const annualLicenseCost = 24000;
const totalFirstYearCost = implementationCost + annualLicenseCost;
const totalAnnualSavings = laborSavings + errorSavings;
const netFirstYearSavings = totalAnnualSavings - totalFirstYearCost;
const roi = Math.round(((totalAnnualSavings - annualLicenseCost) / totalFirstYearCost) * 100);
const paybackMonths = totalFirstYearCost > 0 ? Math.ceil((totalFirstYearCost / totalAnnualSavings) * 12) : 0;
const threeYearValue = (totalAnnualSavings * 3) - implementationCost - (annualLicenseCost * 3);
return {
  hours_saved_per_year: hoursSaved,
  labor_savings: laborSavings,
  error_savings: errorSavings,
  total_annual_savings: totalAnnualSavings,
  implementation_cost: implementationCost,
  annual_license_cost: annualLicenseCost,
  net_first_year: netFirstYearSavings,
  roi_percentage: roi,
  payback_months: paybackMonths,
  three_year_value: threeYearValue,
  calculation_date: new Date().toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "hours_saved_per_year", type: "number" },
            { name: "labor_savings", type: "number" },
            { name: "error_savings", type: "number" },
            { name: "total_annual_savings", type: "number" },
            { name: "implementation_cost", type: "number" },
            { name: "annual_license_cost", type: "number" },
            { name: "net_first_year", type: "number" },
            { name: "roi_percentage", type: "number" },
            { name: "payback_months", type: "number" },
            { name: "three_year_value", type: "number" },
            { name: "calculation_date", type: "string" },
          ],
        },
      }),

      // 5. Scenario builder runCode
      n(
        "5",
        "runCode",
        "Build ROI Scenarios (Conservative, Base, Optimistic)",
        1000,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Generate three scenarios: conservative, base, optimistic
const baseSavings = parseInt(vars.total_annual_savings || "0", 10);
const baseROI = parseInt(vars.roi_percentage || "0", 10);
const basePayback = parseInt(vars.payback_months || "12", 10);
const base3yr = parseInt(vars.three_year_value || "0", 10);
// Conservative: 70% of base
const conservativeSavings = Math.round(baseSavings * 0.7);
const conservativeROI = Math.round(baseROI * 0.7);
const conservativePayback = Math.ceil(basePayback / 0.7);
const conservative3yr = Math.round(base3yr * 0.7);
// Optimistic: 140% of base
const optimisticSavings = Math.round(baseSavings * 1.4);
const optimisticROI = Math.round(baseROI * 1.4);
const optimisticPayback = Math.max(1, Math.floor(basePayback / 1.4));
const optimistic3yr = Math.round(base3yr * 1.4);
const scenarioSummary = "Conservative: $" + conservativeSavings.toLocaleString() + "/yr (" + conservativeROI + "% ROI) | Base: $" + baseSavings.toLocaleString() + "/yr (" + baseROI + "% ROI) | Optimistic: $" + optimisticSavings.toLocaleString() + "/yr (" + optimisticROI + "% ROI)";
return {
  conservative_savings: conservativeSavings,
  conservative_roi: conservativeROI,
  conservative_payback: conservativePayback,
  conservative_3yr: conservative3yr,
  optimistic_savings: optimisticSavings,
  optimistic_roi: optimisticROI,
  optimistic_payback: optimisticPayback,
  optimistic_3yr: optimistic3yr,
  scenario_summary: scenarioSummary
};`,
            outputVariables: [
              { name: "conservative_savings", type: "number" },
              { name: "conservative_roi", type: "number" },
              { name: "conservative_payback", type: "number" },
              { name: "conservative_3yr", type: "number" },
              { name: "optimistic_savings", type: "number" },
              { name: "optimistic_roi", type: "number" },
              { name: "optimistic_payback", type: "number" },
              { name: "optimistic_3yr", type: "number" },
              { name: "scenario_summary", type: "string" },
            ],
          },
        },
      ),

      // 6. Report generator email
      emailNode(
        "6",
        "Deliver ROI Report via Email",
        1240,
        "{contact_email}",
        "Your Custom ROI Report — {roi_percentage}% ROI with ${total_annual_savings}/year Savings",
        "<h2>ROI Analysis Report for {company_name}</h2><p>Dear {contact_name},</p><p>Based on your inputs, here is your personalized return on investment analysis:</p><h3>Your Current State</h3><ul><li>Annual Operating Cost: ${validated_annual_cost}</li><li>Weekly Process Hours: {validated_hours}h</li><li>Team Size: {validated_team_size}</li><li>Error Rate: {validated_error_rate}%</li></ul><h3>Projected Impact</h3><table border='1' cellpadding='8'><tr><th></th><th>Conservative</th><th>Base Case</th><th>Optimistic</th></tr><tr><td>Annual Savings</td><td>${conservative_savings}</td><td><strong>${total_annual_savings}</strong></td><td>${optimistic_savings}</td></tr><tr><td>ROI</td><td>{conservative_roi}%</td><td><strong>{roi_percentage}%</strong></td><td>{optimistic_roi}%</td></tr><tr><td>Payback Period</td><td>{conservative_payback} mo</td><td><strong>{payback_months} mo</strong></td><td>{optimistic_payback} mo</td></tr><tr><td>3-Year Value</td><td>${conservative_3yr}</td><td><strong>${three_year_value}</strong></td><td>${optimistic_3yr}</td></tr></table><h3>Savings Breakdown</h3><ul><li>Time Savings: {hours_saved_per_year} hours/year (${labor_savings})</li><li>Error Reduction: ${error_savings}/year</li></ul><h3>Investment</h3><ul><li>Implementation: ${implementation_cost}</li><li>Annual License: ${annual_license_cost}</li></ul><p><em>Assumptions: {assumptions}</em></p><p><em>Data Quality: {data_quality}. {benchmark_notes}</em></p>",
      ),

      // 7. CRM lead capture HubSpot
      hubspotPostNode(
        "7",
        "Capture ROI Lead in HubSpot CRM",
        1480,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{contact_email}","firstname":"{contact_name}","company":"{company_name}","industry":"{industry}","hs_lead_status":"NEW","notes_last_activity":"ROI Report: {roi_percentage}% ROI, ${total_annual_savings}/yr savings, {payback_months}mo payback. Current cost: ${validated_annual_cost}. Tool: {tool_category}"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          { name: "properties.email", customName: "hs_email", type: "string" },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1405. REAL TIME LANGUAGE TRANSLATOR AGENT (8 nodes)
  // ===================================================================

  "Real Time Language Translator Agent": {
    summary:
      "Receives translation requests via webhook, extracts text data with AI, detects the source language with runCode, performs contextual translation with AI, ensures terminology consistency with runCode, validates translation quality with a condition, preserves formatting with runCode, and distributes translated content via email.",
    nodes: [
      trigger("Webhook: Translation request received", "webhook_app", {
        source: "content_platform",
        event: "content.translation_requested",
        howItStarts:
          "Fires when a user submits content for translation, including the source text, target languages, domain context, and any glossary or terminology preferences",
        setup:
          "Configure webhook from your content management system or translation request form. Set OpenAI API key for AI-powered translation. Configure email for delivery of translated content.",
        inputFields: [],
      }),

      // 2. AI extract text data
      aiNode(
        "2",
        "AI Extract Translation Request Details",
        280,
        "You are a translation management assistant. Extract the source text, target language(s), content domain, formatting requirements, glossary terms, requester email, and priority from the webhook payload. Respond JSON: {source_text: string, target_languages: string, content_domain: string, formatting_type: string, glossary_terms: string, requester_email: string, priority: string, max_length: string, tone: string}",
        "Raw webhook payload: {webhook_payload}. Extract all translation request parameters regardless of payload format.",
        [
          { field: "source_text", as: "source_text", type: "string" },
          { field: "target_languages", as: "target_languages", type: "string" },
          { field: "content_domain", as: "content_domain", type: "string" },
          { field: "formatting_type", as: "formatting_type", type: "string" },
          { field: "glossary_terms", as: "glossary_terms", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "priority", as: "priority", type: "string" },
          { field: "max_length", as: "max_length", type: "string" },
          { field: "tone", as: "tone", type: "string" },
        ],
      ),

      // 3. Language detection runCode
      n("3", "runCode", "Detect Source Language and Prepare Config", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Detect source language patterns and prepare translation config
const text = vars.source_text || "";
const targets = (vars.target_languages || "es").split(",").map(l => l.trim()).filter(Boolean);
// Simple language detection heuristics
const langPatterns = {
  en: /\\b(the|and|is|are|was|were|have|has|this|that|with)\\b/i,
  es: /\\b(el|la|los|las|de|en|que|por|con|una)\\b/i,
  fr: /\\b(le|la|les|des|est|sont|avec|dans|pour|une)\\b/i,
  de: /\\b(der|die|das|und|ist|von|mit|den|ein|eine)\\b/i,
  pt: /\\b(o|a|os|as|de|em|que|por|com|uma)\\b/i
};
let detectedLang = "en";
let highestCount = 0;
for (const [lang, pattern] of Object.entries(langPatterns)) {
  const matches = (text.match(pattern) || []).length;
  if (matches > highestCount) { highestCount = matches; detectedLang = lang; }
}
const langNames = { en: "English", es: "Spanish", fr: "French", de: "German", pt: "Portuguese", ja: "Japanese", zh: "Chinese", ko: "Korean", ar: "Arabic", hi: "Hindi" };
const wordCount = text.split(/\\s+/).filter(Boolean).length;
const charCount = text.length;
return {
  detected_language: detectedLang,
  detected_language_name: langNames[detectedLang] || detectedLang,
  target_language_list: targets.join(", "),
  target_count: targets.length,
  word_count: wordCount,
  char_count: charCount,
  estimated_translation_time: Math.max(1, Math.ceil(wordCount / 500)) + " minutes",
  detection_confidence: highestCount > 5 ? "high" : highestCount > 2 ? "medium" : "low"
};`,
          outputVariables: [
            { name: "detected_language", type: "string" },
            { name: "detected_language_name", type: "string" },
            { name: "target_language_list", type: "string" },
            { name: "target_count", type: "number" },
            { name: "word_count", type: "number" },
            { name: "char_count", type: "number" },
            { name: "estimated_translation_time", type: "string" },
            { name: "detection_confidence", type: "string" },
          ],
        },
      }),

      // 4. AI contextual translation
      aiNode(
        "4",
        "AI Perform Contextual Translation",
        760,
        "You are an expert multilingual translator. Translate the source text into the target language(s) with high accuracy, preserving meaning, tone, cultural nuances, and domain-specific terminology. If glossary terms are provided, use them consistently. Maintain the original formatting structure. Respond JSON: {translated_text: string, translation_notes: string, cultural_adaptations: string, glossary_applied: string, quality_score: number, alternative_phrasings: string}",
        "Source ({detected_language_name}): {source_text}. Target languages: {target_language_list}. Domain: {content_domain}. Tone: {tone}. Glossary: {glossary_terms}. Max length: {max_length}. Provide a high-quality contextual translation.",
        [
          { field: "translated_text", as: "translated_text", type: "string" },
          {
            field: "translation_notes",
            as: "translation_notes",
            type: "string",
          },
          {
            field: "cultural_adaptations",
            as: "cultural_adaptations",
            type: "string",
          },
          { field: "glossary_applied", as: "glossary_applied", type: "string" },
          { field: "quality_score", as: "quality_score", type: "number" },
          {
            field: "alternative_phrasings",
            as: "alternative_phrasings",
            type: "string",
          },
        ],
      ),

      // 5. Terminology consistency runCode
      n("5", "runCode", "Verify Terminology Consistency", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check glossary term consistency in translated output
const glossaryTerms = (vars.glossary_terms || "").split(",").map(t => t.trim().toLowerCase()).filter(Boolean);
const translatedText = (vars.translated_text || "").toLowerCase();
const appliedTerms = glossaryTerms.filter(term => translatedText.includes(term));
const missedTerms = glossaryTerms.filter(term => !translatedText.includes(term));
const consistencyRate = glossaryTerms.length > 0 ? Math.round((appliedTerms.length / glossaryTerms.length) * 100) : 100;
const qualityScore = parseInt(vars.quality_score || "80", 10);
const passesQuality = qualityScore >= 75 && consistencyRate >= 70 ? "yes" : "no";
return {
  terminology_consistency: consistencyRate,
  applied_terms: appliedTerms.join(", ") || "none specified",
  missed_terms: missedTerms.join(", ") || "none",
  overall_quality: passesQuality,
  quality_label: qualityScore >= 90 ? "EXCELLENT" : qualityScore >= 75 ? "GOOD" : qualityScore >= 60 ? "ACCEPTABLE" : "NEEDS_REVIEW",
  review_needed: passesQuality === "no" ? "yes" : "no"
};`,
          outputVariables: [
            { name: "terminology_consistency", type: "number" },
            { name: "applied_terms", type: "string" },
            { name: "missed_terms", type: "string" },
            { name: "overall_quality", type: "string" },
            { name: "quality_label", type: "string" },
            { name: "review_needed", type: "string" },
          ],
        },
      }),

      // 6. Quality validation condition
      n("6", "condition", "Translation Quality Passes Threshold?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "overall_quality", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 7. Format preservation runCode (True path)
      n("7", "runCode", "Preserve Original Formatting in Translation", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Preserve formatting and prepare final output
const formatting = (vars.formatting_type || "plain").toLowerCase();
const translated = vars.translated_text || "";
let finalOutput = translated;
if (formatting === "html") {
  finalOutput = translated; // HTML tags already preserved by AI
} else if (formatting === "markdown") {
  finalOutput = translated; // Markdown syntax preserved
} else {
  finalOutput = translated.replace(/\\n/g, "<br>");
}
const metadata = "Source: " + (vars.detected_language_name || "Unknown") + " → Target: " + (vars.target_language_list || "Unknown") +
  " | Words: " + (vars.word_count || "0") + " | Quality: " + (vars.quality_label || "N/A") +
  " | Terminology: " + (vars.terminology_consistency || "100") + "% consistent" +
  " | Domain: " + (vars.content_domain || "General");
return {
  final_translation: finalOutput,
  translation_metadata: metadata,
  delivery_ready: "yes",
  completed_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "final_translation", type: "string" },
            { name: "translation_metadata", type: "string" },
            { name: "delivery_ready", type: "string" },
            { name: "completed_timestamp", type: "string" },
          ],
        },
      }),

      // 8. Multi-target distribution email
      emailNode(
        "8",
        "Deliver Translated Content via Email",
        1720,
        "{requester_email}",
        "Translation Complete: {detected_language_name} → {target_language_list} | Quality: {quality_label}",
        "<h2>Translation Delivery</h2><p><strong>Source Language:</strong> {detected_language_name} | <strong>Target:</strong> {target_language_list}</p><p><strong>Domain:</strong> {content_domain} | <strong>Quality:</strong> {quality_label} ({quality_score}/100)</p><hr><h3>Original Text</h3><p>{source_text}</p><hr><h3>Translation</h3><p>{final_translation}</p><hr><h3>Translation Notes</h3><p>{translation_notes}</p><p><strong>Cultural Adaptations:</strong> {cultural_adaptations}</p><p><strong>Alternative Phrasings:</strong> {alternative_phrasings}</p><p><strong>Glossary Terms Applied:</strong> {applied_terms}</p><p><em>{translation_metadata}</em></p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1406. RECEIPT COLLECTION BOT (7 nodes)
  // ===================================================================

  "Receipt Collection Bot": {
    summary:
      "Receives receipt submissions via webhook, extracts data with OCR-style runCode, categorizes expenses with AI, links to expense reports with runCode, detects duplicates with a condition, routes approval via email, and archives to Google Sheets.",
    nodes: [
      trigger("Webhook: Receipt submitted for processing", "webhook_app", {
        source: "expense_app",
        event: "receipt.submitted",
        howItStarts:
          "Fires when an employee submits a receipt via the expense management app, email forwarding, or photo upload, including the receipt image data or text content",
        setup:
          "Configure webhook from your expense app or email receipt forwarding. Set OpenAI API key for AI categorization. Configure email for approval routing. Set Google Sheets for archival.",
        inputFields: [],
      }),

      // 2. OCR data extraction runCode
      n("2", "runCode", "Extract Receipt Data via OCR Processing", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse receipt data from webhook payload (simulates OCR extraction)
const payload = vars.webhook_payload || "{}";
let parsed;
try { parsed = JSON.parse(payload); } catch(e) { parsed = {}; }
const rawText = parsed.receipt_text || parsed.ocr_text || parsed.body || JSON.stringify(parsed);
// Extract common receipt fields using patterns
const amountMatch = rawText.match(/\\$?([\\d,]+\\.\\d{2})/);
const dateMatch = rawText.match(/(\\d{1,2}[\\/\\-]\\d{1,2}[\\/\\-]\\d{2,4})/);
const vendorMatch = rawText.match(/(?:from|at|vendor|merchant)[:\\s]+([^\\n,]+)/i);
const amount = amountMatch ? amountMatch[1].replace(/,/g, "") : "0.00";
const receiptDate = dateMatch ? dateMatch[1] : new Date().toISOString().split("T")[0];
const vendor = vendorMatch ? vendorMatch[1].trim() : "Unknown Vendor";
const submitterEmail = parsed.submitter_email || parsed.email || parsed.from || "unknown@company.com";
const submitterName = parsed.submitter_name || parsed.name || "Employee";
return {
  receipt_amount: amount,
  receipt_date: receiptDate,
  vendor_name: vendor,
  submitter_email: submitterEmail,
  submitter_name: submitterName,
  raw_receipt_text: rawText.substring(0, 500),
  receipt_id: "RCT-" + Date.now().toString(36).toUpperCase(),
  extraction_confidence: amountMatch && dateMatch ? "high" : amountMatch ? "medium" : "low"
};`,
          outputVariables: [
            { name: "receipt_amount", type: "string" },
            { name: "receipt_date", type: "string" },
            { name: "vendor_name", type: "string" },
            { name: "submitter_email", type: "string" },
            { name: "submitter_name", type: "string" },
            { name: "raw_receipt_text", type: "string" },
            { name: "receipt_id", type: "string" },
            { name: "extraction_confidence", type: "string" },
          ],
        },
      }),

      // 3. AI categorization
      aiNode(
        "3",
        "AI Categorize Expense",
        520,
        "You are an expense categorization specialist. Based on the vendor name, amount, and receipt text, categorize this expense into the appropriate expense category (Travel, Meals, Office Supplies, Software, Equipment, Marketing, Professional Services, Transportation, Lodging, Other). Also determine if it's reimbursable, assign a cost center, and flag any policy concerns. Respond JSON: {expense_category: string, subcategory: string, is_reimbursable: string, cost_center: string, tax_deductible: string, policy_flags: string, business_justification: string}",
        "Receipt #{receipt_id}: Vendor: {vendor_name}. Amount: ${receipt_amount}. Date: {receipt_date}. Submitter: {submitter_name}. Raw text: {raw_receipt_text}. Categorize this expense.",
        [
          { field: "expense_category", as: "expense_category", type: "string" },
          { field: "subcategory", as: "expense_subcategory", type: "string" },
          { field: "is_reimbursable", as: "is_reimbursable", type: "string" },
          { field: "cost_center", as: "cost_center", type: "string" },
          { field: "tax_deductible", as: "tax_deductible", type: "string" },
          { field: "policy_flags", as: "policy_flags", type: "string" },
          {
            field: "business_justification",
            as: "business_justification",
            type: "string",
          },
        ],
      ),

      // 4. Expense report linkage runCode
      n("4", "runCode", "Link Receipt to Expense Report", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Link this receipt to the current expense report and check for duplicates
const amount = parseFloat(vars.receipt_amount || "0");
const vendor = vars.vendor_name || "Unknown";
const date = vars.receipt_date || "";
const submitter = vars.submitter_email || "";
// Generate a dedup key based on amount + vendor + date
const dedupKey = (amount.toFixed(2) + "-" + vendor.toLowerCase().replace(/\\s+/g, "") + "-" + date).replace(/[^a-z0-9.-]/g, "");
// Determine approval threshold
const requiresApproval = amount > 100 ? "yes" : "no";
const approvalLevel = amount > 1000 ? "director" : amount > 500 ? "manager" : amount > 100 ? "lead" : "auto";
const isDuplicate = "no"; // Would check against stored hashes in production
const reportPeriod = new Date().toISOString().substring(0, 7); // YYYY-MM
return {
  dedup_key: dedupKey,
  is_duplicate: isDuplicate,
  requires_approval: requiresApproval,
  approval_level: approvalLevel,
  report_period: reportPeriod,
  expense_report_id: "EXP-" + reportPeriod + "-" + submitter.split("@")[0],
  linked_status: "PENDING_APPROVAL"
};`,
          outputVariables: [
            { name: "dedup_key", type: "string" },
            { name: "is_duplicate", type: "string" },
            { name: "requires_approval", type: "string" },
            { name: "approval_level", type: "string" },
            { name: "report_period", type: "string" },
            { name: "expense_report_id", type: "string" },
            { name: "linked_status", type: "string" },
          ],
        },
      }),

      // 5. Duplicate detection condition
      n("5", "condition", "Is This a Duplicate Receipt?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "is_duplicate", operator: "eq", value: "no" },
          ],
        },
      }),

      // 6. Approval routing email (True path — not duplicate)
      emailNode(
        "6",
        "Route Receipt for Approval",
        1240,
        "{submitter_email}",
        "Receipt Processed: {receipt_id} — ${receipt_amount} at {vendor_name} ({expense_category})",
        "<h2>Receipt Processing Confirmation</h2><p>Hi {submitter_name},</p><p>Your receipt has been processed and categorized:</p><ul><li><strong>Receipt ID:</strong> {receipt_id}</li><li><strong>Vendor:</strong> {vendor_name}</li><li><strong>Amount:</strong> ${receipt_amount}</li><li><strong>Date:</strong> {receipt_date}</li><li><strong>Category:</strong> {expense_category} / {expense_subcategory}</li><li><strong>Cost Center:</strong> {cost_center}</li><li><strong>Reimbursable:</strong> {is_reimbursable}</li><li><strong>Tax Deductible:</strong> {tax_deductible}</li></ul><p><strong>Expense Report:</strong> {expense_report_id} ({report_period})</p><p><strong>Approval Status:</strong> {linked_status} (Level: {approval_level})</p>{policy_flags}",
      ),

      // 7. Confirmation archive Sheets
      sheetsNode(
        "7",
        "Archive Receipt to Expense Tracker",
        1480,
        "ReceiptArchive",
        '[["{receipt_date}","{receipt_id}","{vendor_name}","{receipt_amount}","{expense_category}","{expense_subcategory}","{cost_center}","{submitter_name}","{submitter_email}","{expense_report_id}","{approval_level}","{is_reimbursable}","{extraction_confidence}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1407. RECEIPT SCANNER AND CATEGORIZER (8 nodes)
  // ===================================================================

  "Receipt Scanner and Categorizer": {
    summary:
      "Receives receipt images via webhook, preprocesses image data with runCode, performs OCR text extraction with runCode, classifies expenses using AI, validates against expense policies with a condition, routes based on policy compliance with runCode, syncs to the accounting system via API, and generates monthly analytics in Google Sheets.",
    nodes: [
      trigger("Webhook: Receipt image ingested", "webhook_app", {
        source: "mobile_app",
        event: "receipt.image_uploaded",
        howItStarts:
          "Fires when an employee uploads a receipt photo through the mobile expense app, email attachment, or document scanner integration",
        setup:
          "Configure webhook from your mobile expense app or email parser. Set OpenAI API key for AI classification. Configure accounting system API (QuickBooks/Xero). Set Google Sheets for analytics tracking.",
        inputFields: [],
      }),

      // 2. Image preprocessing runCode
      n("2", "runCode", "Preprocess Receipt Image Data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Preprocess and validate receipt image data from webhook
const payload = vars.webhook_payload || "{}";
let parsed;
try { parsed = JSON.parse(payload); } catch(e) { parsed = {}; }
const imageUrl = parsed.image_url || parsed.attachment_url || parsed.file_url || "";
const imageFormat = imageUrl.split(".").pop() || "unknown";
const fileSize = parsed.file_size || parsed.size || 0;
const submitterEmail = parsed.user_email || parsed.submitter || parsed.email || "employee@company.com";
const submitterName = parsed.user_name || parsed.submitter_name || "Employee";
const isValidFormat = ["jpg", "jpeg", "png", "pdf", "heic"].includes(imageFormat.toLowerCase());
const isValidSize = fileSize < 10 * 1024 * 1024; // Under 10MB
return {
  image_url: imageUrl,
  image_format: imageFormat,
  file_size_bytes: fileSize,
  submitter_email: submitterEmail,
  submitter_name: submitterName,
  is_valid_image: isValidFormat && (isValidSize || fileSize === 0) ? "yes" : "no",
  preprocessing_notes: !isValidFormat ? "Invalid format: " + imageFormat : !isValidSize ? "File too large: " + fileSize + " bytes" : "Image validated successfully",
  scan_id: "SCN-" + Date.now().toString(36).toUpperCase(),
  scan_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "image_url", type: "string" },
            { name: "image_format", type: "string" },
            { name: "file_size_bytes", type: "number" },
            { name: "submitter_email", type: "string" },
            { name: "submitter_name", type: "string" },
            { name: "is_valid_image", type: "string" },
            { name: "preprocessing_notes", type: "string" },
            { name: "scan_id", type: "string" },
            { name: "scan_timestamp", type: "string" },
          ],
        },
      }),

      // 3. OCR extraction runCode
      n("3", "runCode", "Extract Text from Receipt via OCR", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Simulate OCR text extraction from receipt image
// In production this would call Google Vision API, AWS Textract, or similar
const payload = vars.webhook_payload || "{}";
let parsed;
try { parsed = JSON.parse(payload); } catch(e) { parsed = {}; }
const ocrText = parsed.ocr_result || parsed.extracted_text || parsed.receipt_text || parsed.body || "";
// Parse structured data from OCR text
const lines = ocrText.split("\\n").filter(Boolean);
const amountPattern = /\\$?([\\d,]+\\.\\d{2})/g;
const amounts = [];
let match;
while ((match = amountPattern.exec(ocrText)) !== null) {
  amounts.push(parseFloat(match[1].replace(/,/g, "")));
}
const totalAmount = amounts.length > 0 ? Math.max(...amounts) : 0;
const datePattern = /(\\d{1,2}[\\/\\-]\\d{1,2}[\\/\\-]\\d{2,4})/;
const dateMatch = ocrText.match(datePattern);
const receiptDate = dateMatch ? dateMatch[1] : new Date().toISOString().split("T")[0];
const merchantLine = lines[0] || "Unknown Merchant";
const itemCount = lines.filter(l => l.match(/\\$?[\\d,]+\\.\\d{2}/)).length;
return {
  ocr_extracted_text: ocrText.substring(0, 1000),
  merchant_name: merchantLine.trim(),
  total_amount: totalAmount.toFixed(2),
  receipt_date: receiptDate,
  line_item_count: itemCount,
  ocr_line_count: lines.length,
  extraction_quality: lines.length > 5 ? "high" : lines.length > 2 ? "medium" : "low",
  currency: "USD"
};`,
          outputVariables: [
            { name: "ocr_extracted_text", type: "string" },
            { name: "merchant_name", type: "string" },
            { name: "total_amount", type: "string" },
            { name: "receipt_date", type: "string" },
            { name: "line_item_count", type: "number" },
            { name: "ocr_line_count", type: "number" },
            { name: "extraction_quality", type: "string" },
            { name: "currency", type: "string" },
          ],
        },
      }),

      // 4. AI classification engine
      aiNode(
        "4",
        "AI Classify and Tag Expense",
        760,
        "You are an expense classification engine. Based on the merchant name, receipt text, amount, and line items, classify this expense into the correct GL (General Ledger) account category, department, and tax treatment. Identify the expense type and any compliance flags. Respond JSON: {gl_category: string, gl_code: string, department: string, expense_type: string, tax_treatment: string, is_business_expense: string, compliance_flags: string, merchant_category: string, description: string}",
        "Scan #{scan_id}: Merchant: {merchant_name}. Amount: ${total_amount} {currency}. Date: {receipt_date}. Items: {line_item_count}. OCR text: {ocr_extracted_text}. Submitter: {submitter_name} ({submitter_email}). Classify this expense for accounting.",
        [
          { field: "gl_category", as: "gl_category", type: "string" },
          { field: "gl_code", as: "gl_code", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "expense_type", as: "expense_type", type: "string" },
          { field: "tax_treatment", as: "tax_treatment", type: "string" },
          {
            field: "is_business_expense",
            as: "is_business_expense",
            type: "string",
          },
          { field: "compliance_flags", as: "compliance_flags", type: "string" },
          {
            field: "merchant_category",
            as: "merchant_category",
            type: "string",
          },
          { field: "description", as: "expense_description", type: "string" },
        ],
      ),

      // 5. Policy compliance validator condition
      n("5", "condition", "Expense Within Policy Limits?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "is_business_expense", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 6. Routing engine runCode (True path — compliant)
      n("6", "runCode", "Determine Approval Routing and Priority", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine approval routing based on amount and category
const amount = parseFloat(vars.total_amount || "0");
const category = vars.gl_category || "General";
const flags = vars.compliance_flags || "none";
const hasFlags = flags !== "none" && flags.length > 3;
let approvalRoute = "auto_approved";
let approvalPriority = "low";
if (amount > 5000 || hasFlags) { approvalRoute = "cfo_review"; approvalPriority = "high"; }
else if (amount > 1000) { approvalRoute = "director_review"; approvalPriority = "medium"; }
else if (amount > 250) { approvalRoute = "manager_review"; approvalPriority = "normal"; }
const reimbursementEta = approvalRoute === "auto_approved" ? "3 business days" : approvalRoute === "manager_review" ? "5 business days" : "7-10 business days";
return {
  approval_route: approvalRoute,
  approval_priority: approvalPriority,
  reimbursement_eta: reimbursementEta,
  needs_manual_review: hasFlags ? "yes" : "no",
  routing_reason: amount > 5000 ? "High-value expense" : hasFlags ? "Policy flags detected" : amount > 1000 ? "Above manager threshold" : amount > 250 ? "Above auto-approval limit" : "Within auto-approval range",
  processed_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "approval_route", type: "string" },
            { name: "approval_priority", type: "string" },
            { name: "reimbursement_eta", type: "string" },
            { name: "needs_manual_review", type: "string" },
            { name: "routing_reason", type: "string" },
            { name: "processed_timestamp", type: "string" },
          ],
        },
      }),

      // 7. Accounting system sync API
      n("7", "hitApi", "Sync Expense to Accounting System", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.quickbooks.intuit.com/v3/company/{company_id}/purchase",
          headers: [
            { key: "Authorization", value: "Bearer {quickbooks_token}" },
            { key: "Content-Type", value: "application/json" },
            { key: "Accept", value: "application/json" },
          ],
          body: [
            { key: "PaymentType", value: "Cash" },
            { key: "TotalAmt", value: "{total_amount}" },
            { key: "TxnDate", value: "{receipt_date}" },
            {
              key: "Line",
              value:
                '[{"Amount":"{total_amount}","DetailType":"AccountBasedExpenseLineDetail","AccountBasedExpenseLineDetail":{"AccountRef":{"name":"{gl_category}"},"BillableStatus":"Billable","TaxCodeRef":{"value":"{tax_treatment}"}}}]',
            },
            {
              key: "EntityRef",
              value: '{"name":"{submitter_name}","type":"Vendor"}',
            },
          ],
          variables: [
            {
              name: "Purchase.Id",
              customName: "qb_transaction_id",
              type: "string",
            },
            {
              name: "Purchase.SyncToken",
              customName: "qb_sync_token",
              type: "string",
            },
          ],
        },
      }),

      // 8. Monthly analytics Sheets
      sheetsNode(
        "8",
        "Log to Monthly Expense Analytics",
        1720,
        "ExpenseAnalytics",
        '[["{receipt_date}","{scan_id}","{merchant_name}","{total_amount}","{currency}","{gl_category}","{gl_code}","{department}","{expense_type}","{tax_treatment}","{submitter_name}","{approval_route}","{approval_priority}","{extraction_quality}","{qb_transaction_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
  },
};
